import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { collection, query, where, getDocs, getDoc, doc, updateDoc, addDoc, deleteDoc, setDoc } from 'firebase/firestore';
import { db } from '../config';
import { auth } from '../config';
import { onAuthStateChanged } from 'firebase/auth';
import { useAuth } from '../context/AuthContext';
import { directExcelNotification } from '../services/directExcelNotification';
import excelAwsS3Service, { ExcelWorkbook } from '../services/excelAwsS3Service';
import excelWhatsAppService from '../services/excelWhatsAppService';
import awsS3Service from '../services/awsS3Service'; // NEW: For attachment uploads
import UserSelector from './UserSelector';
import HeaderImportModal from './HeaderImportModal';
import { CORE_HEADERS } from '../utils/headerTemplates';
import './FullExcelEditor.css';

interface Cell {
  value: string | number;
  formula?: string;
  format?: {
    bold?: boolean;
    italic?: boolean;
    color?: string;
    backgroundColor?: string;
    textAlign?: 'left' | 'center' | 'right';
  };
  type?: 'text' | 'number' | 'date' | 'formula';
}

interface ExcelSheet {
  name: string;
  data: Cell[][];
  selectedCell: { row: number; col: number } | null;
  selectedRange: { startRow: number; startCol: number; endRow: number; endCol: number } | null;
}

interface FullExcelEditorProps {
  folderId: string;
  folderName: string;
  onClose: () => void;
  highlightRow?: number;
  assignedTaskData?: any;
  isConsolidatedView?: boolean;
  allTaskRows?: any[];
  headers?: string[];
  isReadOnly?: boolean; // NEW: Read-only mode for users (admins can edit)
}

// Constants for Excel grid dimensions
const ROWS = 1000;
const COLS = 26;


// Function to create default sheet data with proper headers
const createDefaultSheetData = () => {
  const headerRow: Cell[] = CORE_HEADERS.map(header => ({
    value: header,
    format: { bold: true, backgroundColor: '#f0f0f0', textAlign: 'center' as const }
  }));
  
  // Add empty columns to fill up to COLS
  while (headerRow.length < COLS) {
    headerRow.push({
      value: `Column ${headerRow.length + 1}`,
      format: { bold: true, backgroundColor: '#f0f0f0', textAlign: 'center' as const }
    });
  }
  
  // Create empty data rows
  const dataRows: Cell[][] = [];
  for (let i = 0; i < ROWS - 1; i++) {
    const row: Cell[] = Array.from({ length: COLS }, () => ({ value: '' }));
    dataRows.push(row);
  }
  
  return {
    name: 'Sheet1',
    data: [headerRow, ...dataRows],
    selectedCell: { row: 0, col: 0 },
    selectedRange: null
  };
};
const COLUMN_HEADERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'];

const FullExcelEditor: React.FC<FullExcelEditorProps> = ({ folderId, folderName, onClose, highlightRow, assignedTaskData, isConsolidatedView, allTaskRows, headers, isReadOnly = false }) => {
  // Read-only mode for users, editable for admins
  console.log('🔍 FullExcelEditor PROPS RECEIVED:', {
    folderId,
    folderName,
    highlightRow,
    assignedTaskData,
    assignedTaskDataKeys: assignedTaskData ? Object.keys(assignedTaskData) : 'No data',
    assignedTaskDataString: JSON.stringify(assignedTaskData),
    isReadOnly: isReadOnly, // Users: true, Admins: false
    editAccess: !isReadOnly ? 'FULL EDIT ACCESS' : 'READ-ONLY ACCESS'
  });
  const [sheets, setSheets] = useState<ExcelSheet[]>([]);
  const [activeSheetIndex, setActiveSheetIndex] = useState(0);
  const [selectedCell, setSelectedCell] = useState<{ row: number; col: number } | null>(null);
  const [editingCell, setEditingCell] = useState<{ row: number; col: number } | null>(null);
  const [cellValue, setCellValue] = useState<string>('');
  const [formulaBar, setFormulaBar] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [loadingStartTime, setLoadingStartTime] = useState<number>(Date.now());
  const { user: authUser } = useAuth();
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [showContextMenu, setShowContextMenu] = useState<{ x: number; y: number; row: number; col: number } | null>(null);
  const [clipboard, setClipboard] = useState<{ data: Cell[][]; type: 'copy' | 'cut' } | null>(null);
  const [history, setHistory] = useState<ExcelSheet[][]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [columnWidths, setColumnWidths] = useState<{ [key: number]: number }>({});
  const [rowHeights, setRowHeights] = useState<{ [key: number]: number }>({});

  // Function to calculate optimal column width based on content
  const calculateColumnWidth = (content: string, minWidth: number = 150, maxWidth: number = 400): number => {
    if (!content || typeof content !== 'string') return minWidth;
    
    // Calculate width based on character count (roughly 8px per character + padding)
    const charWidth = 8;
    const padding = 24; // Left and right padding
    const calculatedWidth = Math.max(minWidth, Math.min(maxWidth, (content.length * charWidth) + padding));
    
    return calculatedWidth;
  };

  // Function to auto-adjust column widths based on header content
  const autoAdjustColumnWidths = (headers: Cell[]) => {
    const newColumnWidths: { [key: number]: number } = {};
    
    headers.forEach((header, index) => {
      const headerText = String(header?.value || '');
      newColumnWidths[index] = calculateColumnWidth(headerText, 200, 350);
    });
    
    setColumnWidths(newColumnWidths);
    console.log('📏 Auto-adjusted column widths:', newColumnWidths);
  };
  const [isResizing, setIsResizing] = useState<{ type: 'column' | 'row'; index: number } | null>(null);
  const [resizeStart, setResizeStart] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState<'saved' | 'saving' | 'error'>('saved');
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [showAddSheetModal, setShowAddSheetModal] = useState(false);
  const [newSheetName, setNewSheetName] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [showHeaderImportModal, setShowHeaderImportModal] = useState(false);
  const [forceRender, setForceRender] = useState(0);

  // Removed debugging useEffect hooks to prevent potential loops

  const containerRef = useRef<HTMLDivElement>(null);
  const formulaBarRef = useRef<HTMLInputElement>(null);
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastFocusedCellRef = useRef<{ row: number; col: number } | null>(null);
  const lastAutoSaveRef = useRef<number>(0);
  const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isDoubleClickingRef = useRef<boolean>(false);
  // Removed editingTimeoutRef to prevent infinite loops

  // Removed handleCellFocus to prevent infinite loops

  // Memoized cell click handler to prevent unnecessary re-renders
  const handleCellClickMemo = useCallback((row: number, col: number, event: React.MouseEvent) => {
    handleCellClick(row, col, event);
  }, []);

  // Memoized cell double click handler
  const handleCellDoubleClickMemo = useCallback((row: number, col: number) => {
    handleCellDoubleClick(row, col);
  }, []);

  // Memoized keyboard handler
  const handleCellKeyDownMemo = useCallback((event: React.KeyboardEvent, row: number, col: number) => {
    handleCellKeyDown(event, row, col);
  }, []);

  // Memoized context menu handler
  const handleContextMenuMemo = useCallback((event: React.MouseEvent, row: number, col: number) => {
    handleContextMenu(event, row, col);
  }, []);

  // Function to trigger a controlled re-render
  const triggerRerender = useCallback(() => {
    setForceRender(prev => prev + 1);
  }, []);

  // Removed setEditingCellDebounced to prevent infinite loops

  // Set current user from auth context
  useEffect(() => {
    if (authUser) {
      setCurrentUser({
        id: authUser.uid,
        name: authUser.displayName || authUser.email || 'Unknown User',
        email: authUser.email || ''
      });
      console.log('✅ FullExcelEditor: User authenticated:', authUser.uid);
    } else {
      setCurrentUser(null);
      console.log('❌ FullExcelEditor: No user authenticated');
    }
  }, [authUser]);

  // Removed memoizedConsolidatedSheet useMemo to fix initialization error
  // Define createConsolidatedSheet BEFORE useEffect to avoid initialization error
  const createConsolidatedSheet = (taskRows: any[], headers: string[]): ExcelSheet => {
    console.log('📝 Creating consolidated sheet with all assigned tasks...');
    console.log('📋 Task rows count:', taskRows.length);
    console.log('📋 Headers:', headers);
    console.log('📋 First task row structure:', taskRows[0]);
    console.log('📋 First task row data:', taskRows[0]?.data);
    console.log('📋 All task rows:', taskRows);
    console.log('📋 Headers length:', headers?.length);
    console.log('📋 Headers content:', headers);
    
    // Debug: Check if we have valid data
    if (!taskRows || taskRows.length === 0) {
      console.log('⚠️ No task rows provided, creating empty sheet');
    }
    if (!headers || headers.length === 0) {
      console.log('⚠️ No headers provided, using default headers with Attachment column');
      headers = ['Particulars', 'Concerned Person', 'Status', 'Priority', 'Due Date', 'Attachment'];
    }
    
    const rows: Cell[][] = [];
    
    // Create header row with proper formatting
    const headerRow: Cell[] = headers.map(header => ({
      value: header,
      format: { 
        bold: true, 
        backgroundColor: header === 'Concerned Person' ? '#e3f2fd' : '#f0f0f0', 
        textAlign: 'center' as const 
      }
    }));
    rows.push(headerRow);
    
    // Add all task rows as data rows
    taskRows.forEach((taskRow, index) => {
      console.log(`🔍 Processing task row ${index + 1}:`, taskRow);
      console.log(`🔍 Task row data property:`, taskRow.data);
      console.log(`🔍 Task row keys:`, Object.keys(taskRow));
      
      const row: Cell[] = headers.map(header => {
        // Try multiple ways to get the value
        let value = '';
        
        if (taskRow.data && typeof taskRow.data === 'object') {
          // Check if data is a flat object with header keys
          value = taskRow.data[header] || '';
          console.log(`🔍 From taskRow.data["${header}"]:`, value);
        } else if (taskRow[header]) {
          // Check if the taskRow itself has the header as a property
          value = taskRow[header];
          console.log(`🔍 From taskRow["${header}"]:`, value);
        } else if (typeof taskRow === 'object' && taskRow[header]) {
          // Direct property access
          value = taskRow[header];
          console.log(`🔍 From direct taskRow["${header}"]:`, value);
        } else {
          // Try case-insensitive matching
          const lowerHeader = header.toLowerCase();
          const taskRowKeys = Object.keys(taskRow);
          const matchingKey = taskRowKeys.find(key => key.toLowerCase() === lowerHeader);
          if (matchingKey) {
            value = taskRow[matchingKey];
            console.log(`🔍 From case-insensitive match taskRow["${matchingKey}"]:`, value);
          } else if (taskRow.data) {
            const dataKeys = Object.keys(taskRow.data);
            const dataMatchingKey = dataKeys.find(key => key.toLowerCase() === lowerHeader);
            if (dataMatchingKey) {
              value = taskRow.data[dataMatchingKey];
              console.log(`🔍 From case-insensitive data match taskRow.data["${dataMatchingKey}"]:`, value);
            }
          }
        }
        
        console.log(`🔍 Final value for header "${header}":`, value);
        
        // Apply different formatting based on column type
        let backgroundColor = 'white';
        if (header === 'Concerned Person') {
          backgroundColor = '#e3f2fd';
        } else if (header === 'Status') {
          backgroundColor = value === 'TO-DO' ? '#fef3c7' : value === 'IN-PROGRESS' ? '#dbeafe' : '#d1fae5';
        } else if (header === 'Priority') {
          backgroundColor = value === 'High' ? '#fee2e2' : value === 'Medium' ? '#fef3c7' : '#d1fae5';
        }
        
        return {
          value: String(value),
          format: { backgroundColor }
        };
      });
      rows.push(row);
    });
    
    // Add some empty rows for future additions
    for (let i = 0; i < 20; i++) {
      const row: Cell[] = headers.map(header => {
        const isUserColumn = header === 'Concerned Person';
        return { 
          value: '', 
          format: { 
            backgroundColor: isUserColumn ? '#e3f2fd' : 'white' 
          } 
        };
      });
      rows.push(row);
    }
    
    const consolidatedSheet: ExcelSheet = {
      name: 'My Assigned Tasks',
      data: rows,
      selectedCell: { row: 0, col: 0 },
      selectedRange: null
    };
    
    console.log('✅ Created consolidated sheet with', taskRows.length, 'task rows');
    console.log('✅ Consolidated sheet name:', consolidatedSheet.name);
    console.log('✅ Consolidated sheet data rows:', consolidatedSheet.data.length);
    return consolidatedSheet;
  };

  useEffect(() => {
    console.log('🎯 Highlight row:', highlightRow);
    console.log('🔄 Is consolidated view:', isConsolidatedView);
    
    // Handle consolidated view - create single sheet with all assigned tasks
    if (isConsolidatedView) {
      console.log('🔄 Creating consolidated view...');
      
      // Create consolidated sheet directly without memoization
      let consolidatedSheet;
      if (allTaskRows && allTaskRows.length > 0) {
        consolidatedSheet = createConsolidatedSheet(allTaskRows, headers || []);
      } else if (assignedTaskData && Array.isArray(assignedTaskData) && assignedTaskData.length > 0) {
        const fallbackHeaders = headers || ['Particulars', 'Concerned Person', 'Status', 'Priority', 'Due Date', 'Category', 'Description'];
        consolidatedSheet = createConsolidatedSheet(assignedTaskData, fallbackHeaders);
      } else {
        const defaultHeaders = ['Particulars', 'Concerned Person', 'Status', 'Priority', 'Due Date', 'Category', 'Description'];
        consolidatedSheet = createConsolidatedSheet([], defaultHeaders);
      }
      
      console.log('✅ Setting consolidated sheet:', consolidatedSheet.name);
      setSheets([consolidatedSheet]);
      setActiveSheetIndex(0);
      setIsLoading(false);
      console.log('✅ Consolidated view setup complete - ONLY ONE SHEET');
      return;
    }
    
    // First try to load from localStorage for development safety
    if (folderId && !isConsolidatedView) {
      try {
        const savedData = localStorage.getItem(`excel_workbook_${folderId}`);
        if (savedData) {
          const parsedData = JSON.parse(savedData);
          console.log('💾 Loading data from localStorage for development safety');
          
          // Check if the saved data is recent (within last 5 minutes)
          const lastSaved = new Date(parsedData.lastSaved);
          const now = new Date();
          const timeDiff = now.getTime() - lastSaved.getTime();
          
          if (timeDiff < 5 * 60 * 1000) { // 5 minutes
            console.log('✅ Using recent localStorage data');
            setSheets(parsedData.sheets || []);
            setActiveSheetIndex(parsedData.activeSheetIndex || 0);
            setColumnWidths(parsedData.columnWidths || {});
            setRowHeights(parsedData.rowHeights || {});
            setIsLoading(false);
            return; // Don't load from server if we have recent local data
          }
        }
      } catch (error) {
        console.warn('⚠️ Could not load from localStorage:', error);
      }
    }
    
    // Load from server as fallback - but only if we have a valid folderId AND not in consolidated view
    if (folderId && folderId !== 'undefined' && folderId !== 'null' && !isConsolidatedView) {
      console.log('🔄 Loading from server (not consolidated view)');
      // Create async function to handle loadFolderData
      const loadData = async () => {
        try {
          console.log('🔄 Starting loadFolderData...');
          await loadFolderData();
          console.log('✅ loadFolderData completed successfully');
        } catch (error) {
          console.error('❌ Error loading folder data:', error);
          setIsLoading(false);
        }
      };
      loadData();
      
      // Safety timeout to ensure loading is set to false
      setTimeout(() => {
        console.warn('⚠️ Safety timeout: Force setting isLoading to false');
        setIsLoading(false);
      }, 3000); // 3 second safety timeout
    } else if (!isConsolidatedView) {
      console.warn('⚠️ No valid folderId, creating default sheets...');
      const defaultSheets = createDefaultSheetData();
      setSheets([defaultSheets]);
      saveToHistory([defaultSheets]);
      setIsLoading(false);
    } else {
      console.log('✅ Consolidated view - skipping server load');
    }
  }, [folderId, highlightRow, isConsolidatedView]);

  // Debug sheets
  useEffect(() => {
    console.log('📊 Sheets updated:', sheets.length, sheets.map(s => s.name));
    console.log('📊 Active sheet index:', activeSheetIndex);
    console.log('📊 Current active sheet:', sheets[activeSheetIndex]?.name || 'None');
  }, [sheets, activeSheetIndex]);

  // Debug loading state
  useEffect(() => {
    console.log('⏳ Loading state changed:', isLoading);
  }, [isLoading]);

  // Force loading to false when sheets are loaded
  useEffect(() => {
    if (isLoading && sheets && sheets.length > 0) {
      console.log('🔄 Sheets loaded but isLoading is still true, forcing to false');
      setIsLoading(false);
    }
  }, [sheets, isLoading]);

  // Safety timeout to prevent infinite loading - skip for consolidated view
  useEffect(() => {
    if (isLoading && !isConsolidatedView) {
      const safetyTimeout = setTimeout(() => {
        console.warn('⚠️ Safety timeout reached - forcing loading to stop');
        const defaultSheets = createDefaultSheetData();
        setSheets([defaultSheets]);
        saveToHistory([defaultSheets]);
        setIsLoading(false);
      }, 15000); // 15 second safety timeout

      return () => clearTimeout(safetyTimeout);
    }
  }, [isLoading, isConsolidatedView]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
      }
    };
  }, []);

  const createDefaultSheetData = (): ExcelSheet => {
    console.log('📝 Creating default sheet data with CORE_HEADERS...');
    
    // Use CORE_HEADERS from headerTemplates
    const coreHeaders = [...CORE_HEADERS];
    const rows: Cell[][] = [];
    
    // Create header row with proper formatting
    const headerRow: Cell[] = coreHeaders.map(header => ({
      value: header,
      format: { 
        bold: true, 
        backgroundColor: header === 'Concerned Person' ? '#e3f2fd' : '#f0f0f0', 
        textAlign: 'center' as const 
      }
    }));
    rows.push(headerRow);
    
    // Create empty data rows
    for (let i = 1; i < 100; i++) {
      const row: Cell[] = coreHeaders.map(() => ({ value: '' }));
      rows.push(row);
    }
    
    const defaultSheet: ExcelSheet = {
      name: 'Sheet1',
      data: rows,
      selectedCell: { row: 0, col: 0 },
      selectedRange: null
    };
    
    console.log('✅ Default sheet created with CORE_HEADERS:', coreHeaders);
    return defaultSheet;
  };

  // Check if headers are corrupted (duplicates, timestamps, etc.)
  const areHeadersCorrupted = (headers: string[]): boolean => {
    // Check for duplicates
    const uniqueHeaders = new Set(headers);
    if (uniqueHeaders.size !== headers.length) {
      console.warn('⚠️ Duplicate headers detected:', headers);
      return true;
    }
    
    // Check for timestamp headers
    const timestampPattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;
    if (headers.some(header => timestampPattern.test(header))) {
      console.warn('⚠️ Timestamp headers detected:', headers);
      return true;
    }
    
    // Check for generic column names
    const genericPattern = /^Column \d+$/i;
    if (headers.some(header => genericPattern.test(header))) {
      console.warn('⚠️ Generic column headers detected:', headers);
      return true;
    }
    
    return false;
  };

  // Auto-fix corrupted headers in sheets
  const autoFixCorruptedHeaders = (sheets: ExcelSheet[]): ExcelSheet[] => {
    return sheets.map(sheet => {
      // Get headers from row 0 (header row) - not from data rows
      const headers = sheet.data[0]?.map(cell => cell?.value?.toString().trim() || '') || [];
      console.log('🔍 DEBUG: Checking headers in sheet', sheet.name, 'from row 0:', headers);
      
      if (areHeadersCorrupted(headers)) {
        console.log('🔧 Auto-fixing corrupted headers in sheet:', sheet.name);
        
        // Create clean header row
        const cleanHeaders = [...CORE_HEADERS];
        const headerRow: Cell[] = cleanHeaders.map(header => ({
          value: header,
          format: { 
            bold: true, 
            backgroundColor: header === 'Concerned Person' ? '#e3f2fd' : '#f0f0f0', 
            textAlign: 'center' as const 
          }
        }));
        
        // Create new sheet with clean headers
        const newSheet = {
          ...sheet,
          data: [headerRow, ...sheet.data.slice(1)]
        };
        
        // Ensure all data rows have correct number of columns
        for (let rowIndex = 1; rowIndex < newSheet.data.length; rowIndex++) {
          const currentRow = newSheet.data[rowIndex];
          const newRow: Cell[] = [];
          
          for (let colIndex = 0; colIndex < cleanHeaders.length; colIndex++) {
            newRow[colIndex] = currentRow[colIndex] || { value: '' };
          }
          
          newSheet.data[rowIndex] = newRow;
        }
        
        console.log('✅ Headers auto-fixed for sheet:', sheet.name);
        return newSheet;
      }
      
      return sheet;
    });
  };

  const createDefaultSheet = async () => {
    console.log('📝 Creating default sheet...');
    const defaultSheet = createDefaultSheetData();
    
    setSheets([defaultSheet]);
    setActiveSheetIndex(0);
    setColumnWidths({});
    setRowHeights({});
    
    console.log('✅ Default sheet created successfully');
  };

  const loadFromLocalStorage = async () => {
    try {
      const savedData = localStorage.getItem(`excel_workbook_${folderId}`);
      if (savedData) {
        const parsedData = JSON.parse(savedData);
        console.log('💾 Loading from localStorage:', parsedData);
        
        setSheets(parsedData.sheets || []);
        setActiveSheetIndex(parsedData.activeSheetIndex || 0);
        
        // Auto-adjust column widths if not already set
        const savedColumnWidths = parsedData.columnWidths || {};
        if (Object.keys(savedColumnWidths).length === 0 && parsedData.sheets?.[0]?.data?.[0]) {
          autoAdjustColumnWidths(parsedData.sheets[0].data[0]);
        } else {
          setColumnWidths(savedColumnWidths);
        }
        setRowHeights(parsedData.rowHeights || {});
        
        console.log('✅ Data loaded from localStorage successfully');
        return true;
      } else {
        console.log('📝 No localStorage data found, creating default sheet');
        await createDefaultSheet();
        return true;
      }
    } catch (error) {
      console.error('❌ Error loading from localStorage:', error);
      await createDefaultSheet();
      return false;
    }
  };

  const migrateSheetHeaders = (sheets: ExcelSheet[], forceMigration = false): ExcelSheet[] => {
    return sheets.map(sheet => {
      if (!sheet.data || sheet.data.length === 0) return sheet;
      
      const headerRow = sheet.data[0];
      if (!headerRow) return sheet;
      
      // Check if this sheet needs header migration
      const needsMigration = forceMigration || headerRow.some(cell => 
        cell?.value === 'ID' || 
        cell?.value === '#' ||
        (cell?.value && String(cell.value).toLowerCase().includes('column')) ||
        (cell?.value && String(cell.value).toLowerCase().includes('assigned to')) ||
        (cell?.value && String(cell.value).toLowerCase().includes('due date')) ||
        (cell?.value && String(cell.value).toLowerCase().includes('assigned ...')) ||
        (cell?.value && String(cell.value).toLowerCase().includes('concerned ...')) ||
        // Check for timestamps as headers (like "2025-09-29T02:10:47.824Z")
        (cell?.value && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(String(cell.value))) ||
        // Check for duplicate headers
        (cell?.value && headerRow.filter(h => h?.value === cell?.value).length > 1)
      );
      
      if (!needsMigration) {
        console.log(`⏭️ Skipping migration for sheet: ${sheet.name} (no migration needed)`);
        return sheet;
      }
      
      console.log(`🔄 Migrating headers for sheet: ${sheet.name}`);
      console.log(`📋 Old headers:`, headerRow.map(cell => cell?.value));
      
      // Create new header row with updated structure - use CORE_HEADERS for consistency
      const newHeaders = CORE_HEADERS;
      console.log(`📋 New headers:`, newHeaders);
      
      const newHeaderRow: Cell[] = newHeaders.map(header => ({
        value: header,
        format: { 
          bold: true, 
          backgroundColor: header === 'Concerned Person' ? '#e3f2fd' : '#f0f0f0', 
          textAlign: 'center' as const 
        }
      }));
      
      // Also create additional empty columns to match the original structure
      const additionalColumns = Math.max(0, headerRow.length - newHeaders.length);
      for (let i = 0; i < additionalColumns; i++) {
        newHeaderRow.push({
          value: `Column ${newHeaders.length + i + 1}`,
          format: { 
            bold: true, 
            backgroundColor: '#f0f0f0', 
            textAlign: 'center' as const 
          }
        });
      }
      
      // Reorganize data to match new headers
      const reorganizedData = sheet.data.slice(1).map(row => {
        const newRow: Cell[] = [];
        
        // Create a mapping from old headers to new headers
        const oldHeaders = headerRow.map(cell => String(cell?.value || '').toLowerCase());
        console.log(`📋 Old headers for mapping:`, oldHeaders);
        
        // Map old data to new structure
        const totalColumns = newHeaders.length + additionalColumns;
        for (let newIndex = 0; newIndex < totalColumns; newIndex++) {
          let cellValue: string | number = '';
          let cellFormat = {};
          
          if (newIndex < newHeaders.length) {
            // This is one of our standard headers
            const newHeader = newHeaders[newIndex];
            const newHeaderLower = newHeader.toLowerCase();
            let foundIndex = -1;
            
            // Look for exact matches first
            if (newHeaderLower.includes('particulars')) {
              foundIndex = oldHeaders.findIndex(h => h.includes('particulars') || h.includes('title') || h.includes('name'));
            } else if (newHeaderLower.includes('concerned person')) {
              foundIndex = oldHeaders.findIndex(h => h.includes('concerned') || h.includes('assigned to') || h.includes('assigned'));
            } else if (newHeaderLower.includes('title')) {
              foundIndex = oldHeaders.findIndex(h => h.includes('title') && !h.includes('particulars'));
            } else if (newHeaderLower.includes('status')) {
              foundIndex = oldHeaders.findIndex(h => h.includes('status'));
            } else if (newHeaderLower.includes('priority')) {
              foundIndex = oldHeaders.findIndex(h => h.includes('priority'));
            } else if (newHeaderLower.includes('due date')) {
              foundIndex = oldHeaders.findIndex(h => h.includes('due') || h.includes('date') || h.includes('target'));
            } else if (newHeaderLower.includes('category')) {
              foundIndex = oldHeaders.findIndex(h => h.includes('category') || h.includes('type'));
            } else if (newHeaderLower.includes('created by')) {
              foundIndex = oldHeaders.findIndex(h => h.includes('created') || h.includes('author'));
            } else if (newHeaderLower.includes('description')) {
              foundIndex = oldHeaders.findIndex(h => h.includes('description') || h.includes('details'));
            }
            
            // If found by header name, use that data
            if (foundIndex !== -1 && row[foundIndex]?.value) {
              cellValue = row[foundIndex].value;
              cellFormat = row[foundIndex].format || {};
            } else if (newIndex < row.length && row[newIndex]?.value) {
              // Fallback to position-based mapping
              cellValue = row[newIndex].value;
              cellFormat = row[newIndex].format || {};
            }
          } else {
            // This is an additional column - map from old data
            const oldIndex = newIndex;
            if (oldIndex < row.length && row[oldIndex]?.value) {
              cellValue = row[oldIndex].value;
              cellFormat = row[oldIndex].format || {};
            }
          }
          
          newRow.push({
            value: cellValue,
            format: cellFormat
          });
        }
        
        return newRow;
      });
      
      // Update the sheet with new headers and reorganized data
      const updatedSheet = {
        ...sheet,
        data: [newHeaderRow, ...reorganizedData]
      };
      
      console.log(`✅ Migrated headers for sheet: ${sheet.name}`);
      console.log(`📋 Final headers:`, updatedSheet.data[0].map(cell => cell?.value));
      console.log(`📊 Sample data row:`, updatedSheet.data[1]?.map(cell => cell?.value));
      return updatedSheet;
    });
  };

  const loadFolderData = async () => {
    let loadingTimeout: NodeJS.Timeout | null = null;
    
    // Test AWS S3 configuration
    console.log('🔍 Testing AWS S3 Configuration:');
    console.log('AWS S3 Configured:', excelAwsS3Service.isConfigured());
    console.log('Environment Variables:');
    console.log('- REACT_APP_AWS_ACCESS_KEY_ID:', process.env.REACT_APP_AWS_ACCESS_KEY_ID ? '✅ Set' : '❌ Missing');
    console.log('- REACT_APP_AWS_SECRET_ACCESS_KEY:', process.env.REACT_APP_AWS_SECRET_ACCESS_KEY ? '✅ Set' : '❌ Missing');
    console.log('- REACT_APP_S3_BUCKET_NAME:', process.env.REACT_APP_S3_BUCKET_NAME ? '✅ Set' : '❌ Missing');
    console.log('- REACT_APP_AWS_REGION:', process.env.REACT_APP_AWS_REGION || 'us-east-1 (default)');
    
    try {
      setIsLoading(true);
      console.log('🔄 Loading folder data for:', folderId, folderName);
      console.log('🔍 Folder ID type:', typeof folderId, 'Value:', folderId);
      console.log('🔍 Folder Name type:', typeof folderName, 'Value:', folderName);
      
      // Validate folderId
      if (!folderId || folderId === 'undefined' || folderId === 'null') {
        console.error('❌ Invalid folderId:', folderId);
        console.log('🔄 Creating default sheets as fallback...');
        const defaultSheets = createDefaultSheetData();
        setSheets([defaultSheets]);
        saveToHistory([defaultSheets]);
        setIsLoading(false);
        return;
      }
      
      // Set a maximum loading timeout - INCREASED for AWS
      loadingTimeout = setTimeout(() => {
        console.warn('⚠️ Loading timeout reached, creating default sheets');
        const defaultSheets = createDefaultSheetData();
        setSheets([defaultSheets]);
        saveToHistory([defaultSheets]);
        setIsLoading(false);
      }, 30000); // 30 second timeout for AWS
      
      // Check if we're offline
      if (!navigator.onLine) {
        console.warn('⚠️ Offline mode - loading from localStorage only');
        if (loadingTimeout) clearTimeout(loadingTimeout);
        await loadFromLocalStorage();
        return;
      }
      console.log('🌐 Environment info:', {
        hostname: window.location.hostname,
        protocol: window.location.protocol,
        port: window.location.port,
        href: window.location.href
      });
      console.log('👤 Current user:', {
        id: currentUser?.id,
        name: currentUser?.name,
        email: currentUser?.email
      });
      
      // Clear any old metadata-only data first (only if online)
      if (navigator.onLine) {
        try {
          await excelAwsS3Service.clearOldMetadata(folderId);
        } catch (error) {
          console.warn('⚠️ Could not clear old metadata:', error);
        }
      }
      
      // First try to load saved Excel workbook from AWS S3 with shorter timeout
      let savedWorkbook: any = null;
      try {
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('AWS S3 timeout')), 3000)
        );
        
        savedWorkbook = await Promise.race([
          excelAwsS3Service.loadWorkbook(folderId),
          timeoutPromise
        ]);
        
        console.log('📊 Saved workbook result:', {
          found: !!savedWorkbook,
          hasSheets: !!(savedWorkbook?.sheets),
          sheetsCount: savedWorkbook?.sheets?.length || 0,
          sheetNames: savedWorkbook?.sheets?.map((s: any) => s.name) || [],
          workbookId: savedWorkbook?.id || 'No ID',
          requestedFolderId: folderId
        });
      } catch (error) {
        console.warn('⚠️ AWS S3 load failed, trying Firestore fallback:', error);
        
        // Try Firestore fallback with timeout
        try {
          const firestoreTimeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Firestore timeout')), 5000)
          );
          
          savedWorkbook = await Promise.race([
            (excelAwsS3Service as any).loadFromFirestore(folderId),
            firestoreTimeoutPromise
          ]);
          
          console.log('📊 Loaded from Firestore fallback:', {
            found: !!savedWorkbook,
            hasSheets: !!(savedWorkbook?.sheets),
            sheetsCount: savedWorkbook?.sheets?.length || 0
          });
        } catch (firestoreError) {
          console.warn('⚠️ Both AWS S3 and Firestore failed, creating default sheets:', firestoreError);
          if (loadingTimeout) clearTimeout(loadingTimeout);
          // Create default sheets as final fallback
          const defaultSheets = createDefaultSheetData();
          setSheets([defaultSheets]);
          saveToHistory([defaultSheets]);
          console.log('✅ Created default sheets as fallback');
          setIsLoading(false);
          return;
        }
      }
      
      if (savedWorkbook && savedWorkbook.sheets && savedWorkbook.sheets.length > 0) {
        console.log('✅ Loading saved Excel workbook from AWS S3...');
        console.log('📋 Sheets found:', savedWorkbook.sheets.map((s: any) => s.name));
        console.log('📊 First sheet data sample:', savedWorkbook.sheets[0]?.data?.slice(0, 3));
        console.log('📋 First sheet headers:', savedWorkbook.sheets[0]?.data?.[0]?.map((cell: any) => cell?.value));
        
        // Auto-fix corrupted headers before proceeding
        const fixedSheets = autoFixCorruptedHeaders(savedWorkbook.sheets);
        if (fixedSheets !== savedWorkbook.sheets) {
          console.log('🔧 Headers were auto-fixed during loading');
          // Create a new workbook object with fixed sheets
          const fixedWorkbook: ExcelWorkbook = {
            ...savedWorkbook,
            sheets: fixedSheets,
            updatedAt: new Date().toISOString()
          };
          // Save the fixed sheets back to AWS
          await excelAwsS3Service.saveWorkbook(fixedWorkbook);
        }
        
        // If we have assigned task data, populate the existing saved sheet instead of creating new one
        if (assignedTaskData && highlightRow) {
          console.log('🎯 Populating existing saved workbook with assigned task data...');
          if (loadingTimeout) clearTimeout(loadingTimeout);
          
          // Use the existing saved workbook and just populate the assigned row
          const existingSheets = fixedSheets;
          const targetSheet = existingSheets[0]; // Use first sheet
          let targetRow = highlightRow - 1; // Convert to 0-based index
          
          console.log('🔍 DEBUG: highlightRow =', highlightRow, 'targetRow =', targetRow);
          console.log('🔍 DEBUG: targetRow should NOT be 0 (row 0 is headers)');
          
          // CRITICAL: Never populate row 0 (headers row)
          if (targetRow <= 0) {
            console.error('❌ ERROR: Cannot populate row 0 (headers row)! Using row 1 instead.');
            targetRow = 1; // Use row 1 instead of row 0
          }
          
          // Populate the assigned row with task data using ACTUAL headers
          if (targetSheet.data[targetRow]) {
            // Get headers from row 0 (header row) - not from data rows
            const actualHeaders = targetSheet.data[0].map((cell: any) => cell?.value ? cell.value.toString().trim() : '');
            console.log('📋 Using ACTUAL headers from ROW 0 (header row):', actualHeaders);
            
            // Map task data to actual headers
            actualHeaders.forEach((headerName: string, colIndex: number) => {
              if (targetSheet.data[targetRow][colIndex]) {
                // Try to find matching data in assignedTaskData
                let taskValue = assignedTaskData[headerName] || '';
                
                // If no exact match, try common variations
                if (!taskValue) {
                  const lowerHeader = headerName.toLowerCase();
                  const taskKeys = Object.keys(assignedTaskData);
                  
                  for (const key of taskKeys) {
                    const lowerKey = key.toLowerCase();
                    if (lowerKey === lowerHeader || 
                        lowerKey.includes(lowerHeader) || 
                        lowerHeader.includes(lowerKey)) {
                      taskValue = assignedTaskData[key];
                      break;
                    }
                  }
                }
                
                if (taskValue) {
                  targetSheet.data[targetRow][colIndex] = {
                    ...targetSheet.data[targetRow][colIndex],
                    value: String(taskValue)
                  };
                  console.log(`📝 Populated [${targetRow}, ${colIndex}] (${headerName}): "${taskValue}"`);
                }
              }
            });
          }
          
          setSheets(existingSheets);
          saveToHistory(existingSheets);
          console.log('✅ Assigned task data populated in existing saved sheet');
          setIsLoading(false);
          return;
        }
        
        setSheets(fixedSheets);
        
        // Restore column widths and row heights
        if (savedWorkbook.columnWidths) {
          setColumnWidths(savedWorkbook.columnWidths);
        }
        if (savedWorkbook.rowHeights) {
          setRowHeights(savedWorkbook.rowHeights);
        }
        
        saveToHistory(fixedSheets);
        console.log('✅ Saved workbook loaded successfully');
        if (loadingTimeout) clearTimeout(loadingTimeout);
        setIsLoading(false);
        return;
      }
      
      // Only create default sheets if no saved workbook exists
      console.log('📊 No saved workbook found, creating default sheets...');
      
      // Clear any bad localStorage data to ensure clean start
      if (folderId) {
        try {
          localStorage.removeItem(`excel_workbook_${folderId}`);
          console.log('🧹 Cleared localStorage for clean start');
        } catch (error) {
          console.warn('⚠️ Could not clear localStorage:', error);
        }
      }
      
      // If we have assigned task data but no saved workbook, create a default sheet first
      if (assignedTaskData && highlightRow) {
        console.log('🎯 No saved workbook found, creating default sheet with assigned task data...');
        
        // Create a default sheet with proper headers first
        const defaultSheet = createDefaultSheetData();
        let targetRow = highlightRow - 1; // Convert to 0-based index
        
        // CRITICAL: Never populate row 0 (headers row)
        if (targetRow <= 0) {
          console.error('❌ ERROR: Cannot populate row 0 (headers row)! Using row 1 instead.');
          targetRow = 1; // Use row 1 instead of row 0
        }
        
        // Populate the assigned row with task data using the default headers
        if (defaultSheet.data[targetRow]) {
          
          const actualHeaders = defaultSheet.data[0].map((cell: any) => cell?.value ? cell.value.toString().trim() : '');
          console.log('📋 Using default headers:', actualHeaders);
          
          // Map task data to default headers
          actualHeaders.forEach((headerName: string, colIndex: number) => {
            if (defaultSheet.data[targetRow][colIndex]) {
              // Try to find matching data in assignedTaskData
              let taskValue = assignedTaskData[headerName] || '';
              
              // If no exact match, try common variations
              if (!taskValue) {
                const lowerHeader = headerName.toLowerCase();
                const taskKeys = Object.keys(assignedTaskData);
                
                for (const key of taskKeys) {
                  const lowerKey = key.toLowerCase();
                  if (lowerKey === lowerHeader || 
                      lowerKey.includes(lowerHeader) || 
                      lowerHeader.includes(lowerKey)) {
                    taskValue = assignedTaskData[key];
                    break;
                  }
                }
              }
              
              if (taskValue) {
                defaultSheet.data[targetRow][colIndex] = {
                  ...defaultSheet.data[targetRow][colIndex],
                  value: String(taskValue)
                };
                console.log(`📝 Populated [${targetRow}, ${colIndex}] (${headerName}): "${taskValue}"`);
              }
            }
          });
        }
        
        setSheets([defaultSheet]);
        saveToHistory([defaultSheet]);
        console.log('✅ Default sheet created with assigned task data');
        if (loadingTimeout) clearTimeout(loadingTimeout);
        setIsLoading(false);
        
        // Save this sheet to the database
        try {
          const workbook: ExcelWorkbook = {
            id: `workbook_${folderId}`,
            folderId: folderId,
            workbookName: `${folderName} - Excel Workbook`,
            sheets: [defaultSheet],
            columnWidths: {},
            rowHeights: {},
            version: 1,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            createdBy: currentUser?.id || 'unknown',
            updatedBy: currentUser?.id || 'unknown'
          };
          
          await excelAwsS3Service.saveWorkbook(workbook);
          console.log('💾 Default sheet saved to database with ID:', workbook.id);
        } catch (error) {
          console.error('❌ Error saving default sheet:', error);
        }
        
        return;
      }
      
      // Check if this is a "Things" folder
      if (folderName.toLowerCase().includes('things')) {
        // Create Things sheet with predefined structure
        const newSheets: ExcelSheet[] = [
          {
            name: 'Things',
            data: createThingsSheet(),
            selectedCell: null,
            selectedRange: null
          }
        ];
        setSheets(newSheets);
        saveToHistory(newSheets);
        console.log('✅ Created Things sheet');
        
        // Save the Things sheet to database
        try {
          console.log('💾 Saving Things sheet to database...');
          const workbook: ExcelWorkbook = {
            id: `workbook_${folderId}`,
            folderId: folderId,
            workbookName: `${folderName} - Excel Workbook`,
            sheets: newSheets,
            columnWidths: {},
            rowHeights: {},
            version: 1,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            createdBy: currentUser?.id || 'unknown',
            updatedBy: currentUser?.id || 'unknown'
          };
          
          await excelAwsS3Service.saveWorkbook(workbook);
          console.log('✅ Things sheet saved to database successfully');
        } catch (error) {
          console.error('❌ Error saving Things sheet to database:', error);
        }
        return;
      }
      
      // Regular folder - load existing data and create default sheets
      console.log('📊 Creating default sheets for regular folder...');
      const [tasks, plans, blueprints, targets] = await Promise.all([
        loadTasks(),
        loadPlans(),
        loadBlueprints(),
        loadTargets()
      ]);

      // Create Excel sheets
      const newSheets: ExcelSheet[] = [
        {
          name: 'Tasks',
          data: createSheetData(tasks, 'task'),
          selectedCell: null,
          selectedRange: null
        },
        {
          name: 'Plans',
          data: createSheetData(plans, 'plan'),
          selectedCell: null,
          selectedRange: null
        },
        {
          name: 'Blueprints',
          data: createSheetData(blueprints, 'blueprint'),
          selectedCell: null,
          selectedRange: null
        },
        {
          name: 'Targets',
          data: createSheetData(targets, 'target'),
          selectedCell: null,
          selectedRange: null
        }
      ];

      setSheets(newSheets);
      saveToHistory(newSheets);
      console.log('✅ Created default sheets:', newSheets.map(s => s.name));
      
      // Save the default sheets to database
      try {
        console.log('💾 Saving default sheets to database...');
        const workbook: ExcelWorkbook = {
          id: `workbook_${folderId}`,
          folderId: folderId,
          workbookName: `${folderName} - Excel Workbook`,
          sheets: newSheets,
          columnWidths: {},
          rowHeights: {},
          version: 1,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          createdBy: currentUser?.id || 'unknown',
          updatedBy: currentUser?.id || 'unknown'
        };
        
        await excelAwsS3Service.saveWorkbook(workbook);
        console.log('✅ Default sheets saved to database successfully');
    } catch (error) {
        console.error('❌ Error saving default sheets to database:', error);
      }
      
    } catch (error) {
      console.error('❌ Error loading folder data:', error);
      console.log('🔄 Creating default sheets due to error...');
      const defaultSheets = createDefaultSheetData();
      setSheets([defaultSheets]);
      saveToHistory([defaultSheets]);
    } finally {
      if (loadingTimeout) clearTimeout(loadingTimeout);
      setIsLoading(false);
    }
  };


  // Refresh data from database - skip for consolidated view
  const refreshData = async () => {
    if (isConsolidatedView) {
      console.log('✅ Consolidated view - skipping refresh');
      return;
    }
    
    try {
      console.log('🔄 Refreshing data from database...');
      await loadFolderData();
      console.log('✅ Data refreshed successfully');
    } catch (error) {
      console.error('❌ Error refreshing data:', error);
    }
  };

  const createSheetData = (data: any[], type: string): Cell[][] => {
    const rows: Cell[][] = [];
    
    // Create header row
    const headers = getHeaders(type);
    const headerRow: Cell[] = headers.map(header => ({
      value: header,
      format: { 
        bold: true, 
        backgroundColor: header === 'Concerned Person' ? '#e3f2fd' : '#f0f0f0', 
        textAlign: 'center' as const 
      }
    }));
    rows.push(headerRow);

    // Create data rows
    data.forEach((item, index) => {
      const row: Cell[] = headers.map((header, colIndex) => {
        const value = getItemValue(item, header, type);
        return {
          value: value || '',
          type: getCellType(header, value)
        };
      });
      rows.push(row);
    });

    // Fill remaining rows with empty cells - use the same number of columns as header row
    const numColumns = headerRow.length;
    for (let i = rows.length; i < ROWS; i++) {
      const row: Cell[] = Array.from({ length: numColumns }, () => ({ value: '' }));
      rows.push(row);
    }

    return rows;
  };

  const createThingsSheet = (): Cell[][] => {
    const rows: Cell[][] = [];
    
    // Create header row with "Concerned Person" in second column
    const headers = getHeaders('things');
    const headerRow: Cell[] = headers.map((header, index) => ({
      value: header,
      format: { 
        bold: true, 
        backgroundColor: header === 'Concerned Person' ? '#e3f2fd' : '#f0f0f0', 
        textAlign: 'center' as const 
      }
    }));
    rows.push(headerRow);

    // Fill remaining rows with empty cells
    for (let i = 1; i < ROWS; i++) {
      const row: Cell[] = headers.map((header, colIndex) => {
        // All columns start empty - no auto-incrementing IDs
        return { value: '' };
      });
      rows.push(row);
    }

    return rows;
  };

  const getHeaders = (type: string): string[] => {
    // Always return CORE_HEADERS as the default - these are the essential headers
    return CORE_HEADERS;
  };

  const getItemValue = (item: any, header: string, type: string): string | number => {
    const valueMap: { [key: string]: string } = {
      'Particulars': item.title || item.name || item.particulars || '',  // Map Particulars to title field
      'Concerned Person': item.concernedPerson || item.assignedTo || '',
      'Title': item.title || '',
      'Status': item.status || '',
      'Priority': item.priority || '',
      'Due Date': item.dueDate ? new Date(item.dueDate).toLocaleDateString() : '',
      'Category': item.category || '',
      'Created By': item.createdBy || '',
      'Description': item.description || ''
    };
    return valueMap[header] || '';
  };

  const getCellType = (header: string, value: any): 'text' | 'number' | 'date' | 'formula' => {
    if (header.includes('Date')) return 'date';
    if (header === 'Progress' && typeof value === 'number') return 'number';
    if (typeof value === 'number') return 'number';
    if (typeof value === 'string' && value.startsWith('=')) return 'formula';
    return 'text';
  };

  const formatCellValue = (cell: any, header: string): string => {
    if (!cell || !cell.value) return '';
    
    const value = cell.value;
    const headerLower = header.toLowerCase();
    
    // Format based on header type
    if (headerLower.includes('date') || headerLower.includes('due date')) {
      // Format dates properly
      if (value instanceof Date) {
        return value.toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: '2-digit', 
          day: '2-digit' 
        });
      }
      if (typeof value === 'string' && value.match(/\d{4}-\d{2}-\d{2}/)) {
        return new Date(value).toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: '2-digit', 
          day: '2-digit' 
        });
      }
    }
    
    if (headerLower.includes('status')) {
      // Format status with proper casing
      const status = String(value).toUpperCase();
      if (status === 'TODO') return 'TO-DO';
      if (status === 'INPROGRESS') return 'IN-PROGRESS';
      return status;
    }
    
    if (headerLower.includes('priority')) {
      // Format priority with proper casing
      return String(value).toLowerCase();
    }
    
    if (headerLower.includes('concerned person') || headerLower.includes('assigned to')) {
      // Format user assignment properly
      const userValue = String(value);
      if (userValue.includes('(') && userValue.includes('@')) {
        // Already formatted as "Name (email@domain.com)"
        return userValue;
      }
      if (userValue.includes('@')) {
        // Just email, format as "User (email@domain.com)"
        return `User (${userValue})`;
      }
      // Just name, format as "Name"
      return userValue;
    }
    
    // Default formatting
    return String(value);
  };

  const loadTasks = async () => {
    const tasksRef = collection(db, 'tasks');
    const q = query(tasksRef, where('folderId', '==', folderId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  };

  const loadPlans = async () => {
    const plansRef = collection(db, 'plans');
    const q = query(plansRef, where('folderId', '==', folderId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  };

  const loadBlueprints = async () => {
    const blueprintsRef = collection(db, 'blueprints');
    const q = query(blueprintsRef, where('folderId', '==', folderId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  };

  const loadTargets = async () => {
    const targetsRef = collection(db, 'targets');
    const q = query(targetsRef, where('folderId', '==', folderId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  };

  const saveToHistory = (newSheets: ExcelSheet[]) => {
    try {
      const newHistory = history.slice(0, historyIndex + 1);
      newHistory.push(JSON.parse(JSON.stringify(newSheets)));
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
    } catch (error) {
      console.error('❌ Error saving to history:', error);
      // If history is too large, clear it and start fresh
      setHistory([JSON.parse(JSON.stringify(newSheets))]);
      setHistoryIndex(0);
    }
  };

  const handleCellClick = (row: number, col: number, event: React.MouseEvent) => {
    console.log(`🖱️ Cell click started: [${row}, ${col}]`);
    console.log(`🖱️ Event target:`, event.target);
    console.log(`🖱️ Current editingCell before click:`, editingCell);
    console.log(`🖱️ Current selectedCell before click:`, selectedCell);
    console.log(`🔒 Read-only mode:`, isReadOnly);
    event.preventDefault();
    setSelectedCell({ row, col });
    
    // Block editing in read-only mode
    if (isReadOnly && row > 0) { // Allow header editing even in read-only
      console.log('⚠️ Cell editing blocked - Read-only mode active');
      return; // Don't allow editing
    }
    
    // Safety check: Ensure sheets exist
    if (!sheets || sheets.length === 0) {
      console.error('❌ No sheets available');
      return;
    }
    
    // Safety check: Ensure active sheet exists
    if (!sheets[activeSheetIndex]) {
      console.error('❌ Active sheet does not exist, resetting to 0');
      setActiveSheetIndex(0);
      if (!sheets[0]) {
        console.error('❌ Even sheet 0 does not exist');
        return;
      }
    }
    
    // Safety check: Ensure data array exists
    if (!sheets[activeSheetIndex].data) {
      console.error('❌ Sheet data is undefined');
      return;
    }
    
    // Ensure the row exists
    if (!sheets[activeSheetIndex].data[row]) {
      console.log(`⚠️ Row ${row} doesn't exist, creating it`);
      sheets[activeSheetIndex].data[row] = [];
    }
    
    // Ensure the cell exists
    let cell = sheets[activeSheetIndex].data[row][col];
    if (!cell) {
      console.log(`⚠️ Cell [${row}, ${col}] doesn't exist, creating it`);
      cell = { value: '', format: {} };
      sheets[activeSheetIndex].data[row][col] = cell;
      // Update the sheets state to reflect the new cell
      setSheets([...sheets]);
    }
    
    const cellValue = String(cell.value || '');
    setCellValue(cellValue);
    setFormulaBar(cell.formula || cellValue);
    
    console.log(`🖱️ Cell clicked: [${row}, ${col}], value: "${cellValue}"`);
    console.log(`📊 Current editingCell state:`, editingCell);
    
    // DON'T start editing in read-only mode (including headers)
    if (!isReadOnly) {
      // Start editing immediately for better responsiveness (admin only)
      setEditingCell({ row, col });
      console.log(`✏️ Set editingCell to: [${row}, ${col}]`);
      
      // Focus the formula bar for easier editing
      if (formulaBarRef.current) {
        console.log(`🎯 Focusing formula bar`);
        formulaBarRef.current.focus();
        // Select all text for easy replacement
        formulaBarRef.current.select();
      } else {
        console.log(`❌ Formula bar ref is null`);
      }
    } else {
      console.log(`🔒 Read-only mode: Cell selection only, no editing`);
      // Don't set editingCell at all in read-only mode
    }
  };


  const handleCellDoubleClick = (row: number, col: number) => {
    // Block ALL editing in read-only mode (including headers)
    if (isReadOnly) {
      console.log('⚠️ Cell editing blocked - Read-only mode active');
      alert('⚠️ You are in read-only mode.\n\nOnly admins can edit Excel sheets.\n\nContact your admin to make changes.');
      return; // Don't allow editing
    }
    
    setEditingCell({ row, col });
    setSelectedCell({ row, col });
    
    // Safety check: Ensure sheets exist
    if (!sheets || sheets.length === 0) {
      console.error('❌ No sheets available');
      return;
    }
    
    // Safety check: Ensure active sheet exists
    if (!sheets[activeSheetIndex]) {
      console.error('❌ Active sheet does not exist, resetting to 0');
      setActiveSheetIndex(0);
      if (!sheets[0]) {
        console.error('❌ Even sheet 0 does not exist');
        return;
      }
    }
    
    // Safety check: Ensure data array exists
    if (!sheets[activeSheetIndex].data) {
      console.error('❌ Sheet data is undefined');
      return;
    }
    
    // Ensure the row exists
    if (!sheets[activeSheetIndex].data[row]) {
      sheets[activeSheetIndex].data[row] = [];
    }
    
    // Ensure the cell exists
    let cell = sheets[activeSheetIndex].data[row][col];
    if (!cell) {
      cell = { value: '', format: {} };
      sheets[activeSheetIndex].data[row][col] = cell;
      // Update the sheets state to reflect the new cell
      setSheets([...sheets]);
    }
    
    const cellValue = String(cell.value || ''); 
    setCellValue(cellValue);
    setFormulaBar(cell.formula || cellValue);
  };
  const handleCellKeyDown = async (event: React.KeyboardEvent, row: number, col: number) => {
    const { key } = event;
    
    console.log(`⌨️ Key pressed in cell [${row}, ${col}]: "${key}"`);
    console.log(`🔒 Read-only mode:`, isReadOnly);
    
    // Block ALL editing in read-only mode (including headers)
    if (isReadOnly) {
      console.log('⚠️ Keyboard editing blocked - Read-only mode active');
      // Allow navigation keys but block typing
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter', 'Escape'].includes(key)) {
        // Allow navigation only
      } else {
        event.preventDefault();
        alert('⚠️ You are in read-only mode.\n\nOnly admins can edit Excel sheets.');
        return; // Don't allow editing
      }
    }
    
    // If not editing and user types a printable character, start editing
    if (!editingCell && key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      if (isReadOnly) {
        event.preventDefault();
        alert('⚠️ You are in read-only mode.\n\nOnly admins can edit Excel sheets.');
        return; // Block in read-only mode
      }
      event.preventDefault();
      console.log(`✏️ Starting edit mode for cell [${row}, ${col}]`);
      setEditingCell({ row, col });
      setCellValue(key);
      setFormulaBar(key);
      return;
    }
    
    switch (key) {
      case 'Enter':
        event.preventDefault();
        if (editingCell) {
          await saveCellValue(row, col);
          moveToNextRow(row, col);
        } else {
          // Don't start editing in read-only mode
          if (!isReadOnly || row === 0) {
            setEditingCell({ row, col });
          } else {
            moveToNextRow(row, col); // Just navigate
          }
        }
        break;
      case 'Tab':
        event.preventDefault();
        if (editingCell) {
          await saveCellValue(row, col);
        }
        moveToNextCol(row, col);
        break;
      case 'Escape':
        event.preventDefault();
        setEditingCell(null);
        setCellValue('');
        break;
      case 'Delete':
      case 'Backspace':
        console.log(`🗑️ Delete/Backspace key pressed - clearing cell`);
        if (!editingCell) {
          event.preventDefault();
          updateCell(row, col, '');
        }
        break;
      case 'F2':
        event.preventDefault();
        console.log(`✏️ F2 key pressed - starting edit mode`);
        // Don't start editing in read-only mode
        if (!isReadOnly || row === 0) {
          setEditingCell({ row, col });
        }
        break;
      case 'ArrowUp':
      case 'ArrowDown':
      case 'ArrowLeft':
      case 'ArrowRight':
        if (editingCell) {
          // Let the input handle arrow keys for text editing
          return;
        }
        // Handle navigation when not editing
        event.preventDefault();
        handleKeyDown(event);
        break;
    }
  };

  const handleFormulaBarChange = (value: string) => {
    console.log(`📝 Formula bar changed to: "${value}"`);
    setFormulaBar(value);
    if (selectedCell) {
      console.log(`📝 Updating cell [${selectedCell.row}, ${selectedCell.col}] with value: "${value}"`);
      setCellValue(value);
      // Don't update cell immediately - wait for Enter key or blur
    } else {
      console.log(`❌ No selected cell for formula bar update`);
    }
  };

  const handleFormulaBarKeyDown = async (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && selectedCell) {
      event.preventDefault();
      console.log(`💾 Saving cell [${selectedCell.row}, ${selectedCell.col}] from formula bar`);
      await saveCellValue(selectedCell.row, selectedCell.col);
      // Start editing the cell after pressing Enter in formula bar
      setEditingCell(selectedCell);
    } else if (event.key === 'Escape' && selectedCell) {
      event.preventDefault();
      // Cancel editing and restore original value
      const cell = sheets[activeSheetIndex].data[selectedCell.row][selectedCell.col];
      setCellValue(String(cell.value || ''));
      setFormulaBar(cell.formula || String(cell.value || ''));
    }
  };

  const saveCellValue = async (row: number, col: number) => {
    const value = cellValue.trim();
    const formula = formulaBar.trim();
    
    console.log(`💾 saveCellValue called with: row=${row}, col=${col}, value="${value}"`);
    console.log(`💾 Current editingCell state:`, editingCell);
    console.log(`💾 Current selectedCell state:`, selectedCell);
    
    await updateCell(row, col, value, formula);
    setEditingCell(null);
  };

  const updateCell = async (row: number, col: number, value: string, formula?: string) => {
    console.log(`🔄 Updating cell [${row}, ${col}] with value: "${value}"`);
    console.log(`🔍 Current selectedCell:`, selectedCell);
    console.log(`🔍 Current editingCell:`, editingCell);
    console.log(`🔍 Active sheet index:`, activeSheetIndex);
    console.log(`🔍 Total sheets:`, sheets.length);
    
    const newSheets = [...sheets];
    const cell: Cell = {
      value: value,
      formula: formula,
      type: getCellType('', value)
    };
    
    // Ensure the row and column exist
    if (!newSheets[activeSheetIndex].data[row]) {
      console.log(`⚠️ Row ${row} doesn't exist, creating it`);
      newSheets[activeSheetIndex].data[row] = [];
    }
    
    // Ensure the column exists in the row
    while (newSheets[activeSheetIndex].data[row].length <= col) {
      newSheets[activeSheetIndex].data[row].push({ value: '' });
    }
    
    console.log(`📝 Before update - Cell [${row}, ${col}] current value:`, newSheets[activeSheetIndex].data[row][col]?.value);
    newSheets[activeSheetIndex].data[row][col] = cell;
    console.log(`✅ Cell updated successfully: [${row}, ${col}] = "${value}"`);
    console.log(`🔍 Cell data after update:`, newSheets[activeSheetIndex].data[row][col]);
    console.log(`📊 Row ${row} data:`, newSheets[activeSheetIndex].data[row].map(c => c?.value));
    setSheets(newSheets);
    saveToHistory(newSheets);
    
    // Check if this is a user assignment field and send Excel notification
    if (row > 0 && value && value.trim()) {
      const sheet = newSheets[activeSheetIndex];
      const headerRow = sheet.data[0];
      const fieldName = String(headerRow[col]?.value || '');
      
      // Check if this is an assignment field
      const isAssignmentField = fieldName.toLowerCase().includes('assigned to') ||
                               fieldName.toLowerCase().includes('assigned_to') ||
                               fieldName.toLowerCase().includes('concerned person') ||
                               fieldName.toLowerCase().includes('concerned_person') ||
                               fieldName.toLowerCase().includes('responsible') ||
                               fieldName.toLowerCase().includes('assignee');
      
      if (isAssignmentField) {
        console.log(`🔔 Excel Notification: Detected assignment field "${fieldName}" with value "${value}"`);
        
        // Try to find the user by name or email
        try {
          const { collection, query, where, getDocs } = await import('firebase/firestore');
          const { db } = await import('../config');
          
          // Extract name and email from display format "Name (email@domain.com)"
          let searchName = value;
          let searchEmail = '';
          
          // Check if value is in format "Name (email@domain.com)"
          const emailMatch = value.match(/^(.+?)\s*\(([^)]+)\)$/);
          if (emailMatch) {
            searchName = emailMatch[1].trim();
            searchEmail = emailMatch[2].trim();
            console.log(`🔍 Extracted name: "${searchName}", email: "${searchEmail}"`);
          }
          
          // Search for user by name first
          let usersQuery = query(
            collection(db, 'users'),
            where('name', '==', searchName)
          );
          let usersSnapshot = await getDocs(usersQuery);
          console.log(`🔍 Search by name "${searchName}": Found ${usersSnapshot.docs.length} users`);
          
          // If not found by name, try by email
          if (usersSnapshot.empty && searchEmail) {
            console.log(`🔍 User not found by name, trying email: ${searchEmail}`);
            usersQuery = query(
              collection(db, 'users'),
              where('email', '==', searchEmail)
            );
            usersSnapshot = await getDocs(usersQuery);
            console.log(`🔍 Search by email "${searchEmail}": Found ${usersSnapshot.docs.length} users`);
          }
          
          // If still not found, try by displayName
          if (usersSnapshot.empty) {
            console.log(`🔍 User not found by name/email, trying displayName: ${searchName}`);
            usersQuery = query(
              collection(db, 'users'),
              where('displayName', '==', searchName)
            );
            usersSnapshot = await getDocs(usersQuery);
            console.log(`🔍 Search by displayName "${searchName}": Found ${usersSnapshot.docs.length} users`);
          }
          
          if (!usersSnapshot.empty) {
            const userDoc = usersSnapshot.docs[0];
            const assignedUser = userDoc.data();
            const cellAddress = `${String.fromCharCode(65 + col)}${row + 1}`;
            
            // Extract user name with fallbacks
            const userName = assignedUser.name || assignedUser.displayName || assignedUser.email || 'Unknown User';
            const adminName = authUser?.displayName || authUser?.email || 'Admin';
            
            console.log(`🔔 Excel Notification: Found user ${userName} (${userDoc.id})`);
            console.log(`🔍 User data:`, {
              id: userDoc.id,
              name: assignedUser.name,
              displayName: assignedUser.displayName,
              email: assignedUser.email,
              fullData: assignedUser
            });
            console.log(`👤 Admin details:`, {
              uid: authUser?.uid,
              displayName: authUser?.displayName,
              email: authUser?.email
            });
            
            // Send DIRECT notification with complete row data
            try {
              console.log(`⚡ Sending DIRECT Excel notification (via updateCell) to ${userName}...`);
              
              // Get the complete Excel data
              const currentSheet = sheets[activeSheetIndex];
              const headerRow = currentSheet.data[0];
              const dataRow = currentSheet.data[row];
              
              // Extract headers
              const headers = headerRow.map((cell: any) => cell?.value ? cell.value.toString().trim() : '');
              
              // Extract row data
              const rowData = dataRow.map((cell: any) => cell?.value ? cell.value.toString().trim() : '');
              
              console.log(`📊 DIRECT NOTIFICATION (updateCell): Headers:`, headers);
              console.log(`📊 DIRECT NOTIFICATION (updateCell): Row data:`, rowData);
              
              // Check if row has data
              const hasData = rowData.some((cell, index) => {
                if (index === 1) return false; // Skip "Concerned Person" column
                return cell && cell.trim() !== '';
              });
              
              if (!hasData) {
                console.warn(`⚠️ WARNING: Row ${row} has no data! Notification will contain empty data.`);
              }
              
              // Send direct notification
              const result = await directExcelNotification.sendDirectExcelNotification(
                userDoc.id,
                {
                  headers: headers,
                  rows: [rowData], // Single row array
                  sheetName: sheet.name,
                  workbookId: folderId
                },
                adminName,
                0 // Index 0 because we're sending single row in array
              );
              
              if (result.success) {
                console.log(`✅ DIRECT notification sent to ${userName} successfully`);
                if (hasData) {
                  console.log(`✅ Notification contains data:`, rowData);
                } else {
                  console.warn(`⚠️ Notification sent but contains EMPTY data!`);
                }
              } else {
                console.error(`❌ DIRECT notification failed for ${userName}:`, result.error);
              }
            } catch (error) {
              console.error(`❌ Failed to send DIRECT notification (updateCell) to ${userName}:`, error);
            }
            
            console.log(`✅ Excel Notification: Sent assignment notification to ${userName}`);
          } else {
            console.log(`⚠️ Excel Notification: User "${value}" not found in database`);
            console.log(`🔍 Searched for: name="${searchName}", email="${searchEmail}"`);
          }
        } catch (error) {
          console.error('❌ Excel Notification: Error sending assignment notification:', error);
        }
      }
    }
    
    // Save to database if it's a data row (not header)
    if (row > 0) {
      console.log(`💾 Cell updated: Row ${row}, Col ${col}, Value: ${value}`);
      console.log(`💾 Verification - editingCell:`, editingCell);
      console.log(`💾 Verification - selectedCell:`, selectedCell);
      
      // Ensure we're saving to the correct cell
      const targetRow = editingCell?.row || selectedCell?.row || row;
      const targetCol = editingCell?.col || selectedCell?.col || col;
      
      console.log(`💾 Target coordinates: [${targetRow}, ${targetCol}]`);
      
      if (targetRow !== row || targetCol !== col) {
        console.log(`⚠️ WARNING: Coordinate mismatch! updateCell called with [${row}, ${col}] but target is [${targetRow}, ${targetCol}]`);
      }
      
      saveToDatabase(row, col, value, formula);
      
      // Clear any existing auto-save timeout to prevent multiple saves
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
      
      // Trigger debounced auto-save for important changes (less frequent to prevent blinking)
      autoSaveTimeoutRef.current = setTimeout(() => {
        console.log('🔄 Triggering debounced auto-save after cell update...');
        handleAutoSave();
        autoSaveTimeoutRef.current = null;
      }, 5000); // Increased to 5 seconds to reduce frequency and prevent blinking
    } else {
      console.log(`📝 Header cell updated: [${row}, ${col}] = "${value}" (not saved to database)`);
    }
  };

  const saveToDatabase = async (row: number, col: number, value: string, formula?: string) => {
    try {
      const sheet = sheets[activeSheetIndex];
      // Use actual headers from the sheet instead of default headers
      const headers = sheet.data[0].map((cell: any) => String(cell?.value || '').trim());
      const fieldName = headers[col];
      
      if (!fieldName || row === 0) return; // Skip header row

      // Find the item ID from the first column
      const itemId = sheet.data[row][0]?.value;
      if (!itemId) return;

      const collectionName = sheet.name.toLowerCase();
      const docRef = doc(db, collectionName, String(itemId));
      
      // Map field names to database fields
      const fieldMap: { [key: string]: string } = {
        'ID': 'id',
        'Particulars': 'title',  // Map Particulars to title field in database
        'Title': 'title',
        'Status': 'status',
        'Priority': 'priority',
        'Assigned To': 'assignedTo',
        'Due Date': 'dueDate',
        'Created By': 'createdBy',
        'Description': 'description',
        'Start Date': 'startDate',
        'End Date': 'endDate',
        'Progress': 'progress',
        'Owner': 'owner',
        'Version': 'version',
        'Category': 'category',
        'Target Date': 'targetDate',
        'Concerned Person': 'concernedPerson',
        'Responsible': 'responsible',
        'Assigned': 'assignedTo',
        'Assignee': 'assignedTo',
        'Field 1': 'field1',
        'Field 2': 'field2',
        'Field 3': 'field3',
        'Field 4': 'field4',
        'Field 5': 'field5',
        'Field 6': 'field6',
        'Field 7': 'field7',
        'Field 8': 'field8'
      };

      const dbFieldName = fieldMap[fieldName];
      if (!dbFieldName) return;

      const updateData: any = {};
      
      // Handle date fields
      if (fieldName.includes('Date')) {
        if (value) {
          const date = new Date(value);
          if (!isNaN(date.getTime())) {
            updateData[dbFieldName] = date.toISOString();
          }
        } else {
          updateData[dbFieldName] = null;
        }
      } else {
        updateData[dbFieldName] = value;
      }
      
      updateData.updatedAt = new Date().toISOString();
      updateData.updatedBy = currentUser?.id;

      console.log('Saving to database:', { collectionName, itemId, fieldName, dbFieldName, value, updateData });
      
      // Get existing document data first
      const existingDoc = await getDoc(docRef);
      let existingData = {};
      
      if (existingDoc.exists()) {
        existingData = existingDoc.data();
      }
      
      // Merge with existing data
      const mergedData = { ...existingData, ...updateData };
      await setDoc(docRef, mergedData, { merge: true });
      
      // Send Excel notification for the update
      await sendExcelNotification(sheet.name, String(itemId), fieldName, value, row, col);
      
      // Notify concerned persons
      await notifyConcernedPersons(sheet.name, String(itemId), fieldName, value, row, col);
      
      console.log('✅ Successfully saved to database');
      
    } catch (error) {
      console.error('❌ Error saving to database:', error);
    }
  };

  // Save entire Excel workbook to AWS S3
  const saveEntireWorkbook = async () => {
    try {
      setAutoSaveStatus('saving');
      console.log('💾 Saving complete Excel workbook to AWS S3...');
      
      // Get current sheets state directly
      const currentSheets = sheets;
      console.log('📊 All sheets (direct):', currentSheets);
      console.log('📊 Sheets data details:', currentSheets.map(s => ({
        name: s.name,
        rowCount: s.data.length,
        sampleData: s.data.slice(0, 3).map(row => row.map(cell => cell?.value || ''))
      })));
      
      // Use authUser if currentUser is null (timing issue fix)
      const userToUse = currentUser || (authUser ? {
        id: authUser.uid,
        name: authUser.displayName || authUser.email || 'Unknown User',
        email: authUser.email || ''
      } : null);
      
      console.log('👤 Current user:', currentUser);
      console.log('👤 Auth user:', authUser);
      console.log('👤 User to use for save:', userToUse);
      console.log('📁 Folder ID:', folderId);
      
      if (!authUser || !userToUse) {
        console.error('❌ No authenticated user found');
        console.log('🔍 Auth user:', authUser);
        console.log('🔍 User to use:', userToUse);
        
        // Don't set error status immediately - try localStorage fallback first
        console.log('💾 No authenticated user, trying localStorage fallback...');
        try {
          const fallbackData = {
            sheets: sheets,
            activeSheetIndex,
            columnWidths,
            rowHeights,
            lastSaved: new Date().toISOString()
          };
          
          localStorage.setItem(`excel_workbook_${folderId}`, JSON.stringify(fallbackData));
          console.log('✅ Saved to localStorage (no auth user)');
          
          setAutoSaveStatus('saved');
          setLastSaved(new Date());
          
          alert('✅ Data saved to browser storage (localStorage).\n\nNote: You are not logged in, so data is saved locally only.\nTo enable cloud storage, please log in and configure AWS S3.');
          
          return true;
        } catch (fallbackError) {
          console.error('❌ localStorage fallback failed:', fallbackError);
          setAutoSaveStatus('error');
          alert('❌ Failed to save data. Please log in and try again.');
          return false;
        }
      }
      
      // Check if we have sheets to save
      if (!currentSheets || currentSheets.length === 0) {
        console.log('⚠️ No sheets to save, skipping save operation');
        console.log('🔍 Debug: sheets state is empty, this might be a timing issue');
        console.log('🔍 Debug: Current sheets state:', currentSheets);
        console.log('🔍 Debug: Sheets length:', currentSheets?.length || 0);
        setAutoSaveStatus('saved');
        return true; // Return true to indicate "success" (nothing to save)
      }
      
      // Additional check: ensure sheets have actual data
      const hasData = currentSheets.some(sheet => 
        sheet.data && sheet.data.length > 0 && 
        sheet.data.some((row: any) => row.some((cell: any) => cell?.value && String(cell.value).trim() !== ''))
      );
      
      if (!hasData) {
        console.log('⚠️ Sheets exist but contain no data, skipping save operation');
        console.log('🔍 Debug: Sheets have no meaningful data to save');
        setAutoSaveStatus('saved');
        return true; // Return true to indicate "success" (nothing to save)
      }
      
      // Get the existing workbook ID or create a new one if none exists
      let workbookId = `workbook_${folderId}`;
      let existingWorkbook = null;
      
      try {
        existingWorkbook = await excelAwsS3Service.loadWorkbook(folderId);
        if (existingWorkbook) {
          workbookId = existingWorkbook.id;
          console.log('📋 Updating existing workbook:', workbookId);
        } else {
          console.log('📋 Creating new workbook:', workbookId);
        }
      } catch (error) {
        console.log('📋 No existing workbook found, creating new one:', workbookId);
      }
      
      // Create workbook object
      const workbook: ExcelWorkbook = {
        id: workbookId,
        folderId: folderId,
        workbookName: `${folderName} - Excel Workbook`,
        sheets: currentSheets,
        columnWidths: columnWidths,
        rowHeights: rowHeights,
        version: existingWorkbook ? (existingWorkbook.version || 1) + 1 : 1,
        createdAt: existingWorkbook?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: existingWorkbook?.createdBy || authUser.uid,
        updatedBy: authUser.uid
      };
      
      // Save to AWS S3
      const result = await excelAwsS3Service.saveWorkbook(workbook, (progress) => {
        console.log(`📊 Upload progress: ${progress.percentage}%`);
      });
      
      if (result.success) {
        setAutoSaveStatus('saved');
        setLastSaved(new Date());
        console.log('✅ Complete Excel workbook saved to AWS S3 successfully');
        
        // Also save individual rows for compatibility
        await saveIndividualRows(userToUse);
        
        // Trigger a custom event to notify other components that data has been saved
        window.dispatchEvent(new CustomEvent('excelDataSaved', { 
          detail: { folderId, sheetName: sheets[activeSheetIndex]?.name } 
        }));
        
        return true;
      } else {
        throw new Error(result.error || 'Failed to save workbook');
      }
      
    } catch (error) {
      console.error('❌ Error saving complete workbook to AWS S3:', error);
      console.error('❌ Error details:', error instanceof Error ? error.message : String(error));
      
      // If AWS S3 fails, try to save to localStorage as fallback
      try {
        console.log('💾 AWS S3 save failed, trying localStorage fallback...');
        const fallbackData = {
          sheets: sheets,
          activeSheetIndex,
          columnWidths,
          rowHeights,
          lastSaved: new Date().toISOString()
        };
        
        localStorage.setItem(`excel_workbook_${folderId}`, JSON.stringify(fallbackData));
        console.log('✅ Saved to localStorage as fallback');
        
        setAutoSaveStatus('saved');
        setLastSaved(new Date());
        
        // Show user-friendly message
        alert('⚠️ Data saved to browser storage (localStorage) instead of cloud storage.\n\nThis is because AWS S3 is not configured yet.\nYour data is safe but will only be available on this device.\n\nTo enable cloud storage, configure AWS S3 credentials.');
        
        return true;
      } catch (fallbackError) {
        console.error('❌ Even localStorage fallback failed:', fallbackError);
        setAutoSaveStatus('error');
        
        // Show user-friendly error message
        alert('❌ Failed to save data to both cloud storage and browser storage.\n\nPlease check your internet connection and try again.\nIf the problem persists, contact your administrator.');
        
        return false;
      }
    }
  };

  // Save individual rows for compatibility
  const saveIndividualRows = async (userToUse: any) => {
    try {
      console.log('💾 Saving individual rows for compatibility...');
      
      // Get current sheets state
      const currentSheets = sheets;
      let totalSaved = 0;
      
      // Save each sheet
      for (const sheet of currentSheets) {
        const collectionName = sheet.name.toLowerCase();
        console.log(`📄 Processing sheet: ${sheet.name}`);
        
        // Also save individual rows for easy querying
        for (let row = 1; row < sheet.data.length; row++) {
          const rowData = sheet.data[row];
          const itemId = rowData[0]?.value;
          
          if (!itemId || itemId === '') continue; // Skip empty rows
          
          const docData: any = {
            id: itemId,
            folderId: folderId,
            sheetName: sheet.name,
            rowIndex: row,
            rowData: rowData,
            updatedAt: new Date().toISOString(),
            updatedBy: userToUse?.id,
            createdBy: userToUse?.id
          };
          
          // Map all columns to document data
          const headers = getHeaders(sheet.name.toLowerCase());
          
          headers.forEach((header, colIndex) => {
            const cellValue = rowData[colIndex]?.value;
            if (cellValue !== undefined && cellValue !== '') {
              const fieldName = header.toLowerCase().replace(/\s+/g, '');
              
              // Handle date fields
              if (header.includes('Date')) {
                const date = new Date(cellValue);
                if (!isNaN(date.getTime())) {
                  docData[fieldName] = date.toISOString();
                }
              } else {
                docData[fieldName] = cellValue;
              }
            }
          });
          
          // Save individual row
          const docRef = doc(db, collectionName, String(itemId));
          await setDoc(docRef, docData, { merge: true });
          totalSaved++;
        }
        
        console.log(`✅ Sheet ${sheet.name} individual rows saved`);
      }
      
      console.log(`✅ Individual rows saved successfully - ${totalSaved} total rows saved`);
      
    } catch (error) {
      console.error('❌ Error saving individual rows:', error);
    }
  };

  // Save entire sheet data to database (legacy function)
  const saveEntireSheet = async () => {
    await saveEntireWorkbook();
  };

  // Send Excel notification for any cell update
  const sendExcelNotification = async (sheetName: string, itemId: string, fieldName: string, newValue: any, row: number, col: number) => {
    try {
      console.log(`🔔 Sending Excel notification for ${fieldName} = ${newValue}`);
      console.log(`🔔 Current user: ${currentUser?.id} (${currentUser?.name})`);
      
      // Get the item to find concerned persons
      const collectionName = sheetName.toLowerCase();
      const docRef = doc(db, collectionName, String(itemId));
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const itemData = docSnap.data();
        const concernedPersons = [];
        
        // Add assigned users
        if (itemData.assignedTo) {
          concernedPersons.push(itemData.assignedTo);
        }
        if (itemData.assignedUsers) {
          concernedPersons.push(...itemData.assignedUsers);
        }
        if (itemData.createdBy) {
          concernedPersons.push(itemData.createdBy);
        }
        
        console.log(`🔔 Found concerned persons:`, concernedPersons);
        
        // Send notifications to concerned persons
        for (const personId of concernedPersons) {
          if (personId !== currentUser?.id) {
            console.log(`🔔 Sending notification to user: ${personId}`);
            // OLD SERVICE REMOVED - Using directExcelNotification instead
            console.log(`✅ Notification sent to user: ${personId}`);
          } else {
            console.log(`⚠️ Skipping notification to current user: ${personId}`);
          }
        }
        
        // If no concerned persons found, send to current user as a test
        if (concernedPersons.length === 0) {
          console.log(`🔔 No concerned persons found, sending test notification to current user`);
          // OLD SERVICE REMOVED - Using directExcelNotification instead
        }
      } else {
        console.log(`⚠️ Document not found: ${collectionName}/${itemId}`);
      }
    } catch (error) {
      console.error('❌ Error sending Excel notification:', error);
    }
  };

  const notifyConcernedPersons = async (sheetName: string, itemId: string, fieldName: string, newValue: any, row: number, col: number) => {
    try {
      console.log(`🔔 Notifying concerned persons for ${fieldName} = ${newValue}`);
      
      // Check if this is a user assignment field (Concerned Person, Assigned To, etc.)
      const headerValue = String(sheets[activeSheetIndex].data[0][col]?.value || '');
      const isUserAssignmentField = headerValue.toLowerCase().includes('concerned person') || 
                                   headerValue.toLowerCase().includes('assigned to') ||
                                   headerValue.toLowerCase().includes('assigned') ||
                                   headerValue.toLowerCase().includes('responsible');
      
      if (isUserAssignmentField && newValue) {
        console.log(`🔔 Detected user assignment field: "${headerValue}"`);
        
        // Parse multiple users from the newValue (comma-separated names with emails)
        const userNames = newValue.split(',').map((name: string) => name.trim()).filter((name: string) => name);
        
        console.log(`🔔 Found ${userNames.length} users to notify:`, userNames);
        
        // Get user IDs from names (case-insensitive)
        const usersCollection = collection(db, 'users');
        const usersSnapshot = await getDocs(usersCollection);
        const userMap = new Map();
        
        usersSnapshot.forEach((doc) => {
          const userData = doc.data();
          const userName = userData.name || userData.displayName || userData.email || 'Unknown User';
          // Store both exact and lowercase versions for flexible matching
          userMap.set(userName, doc.id);
          userMap.set(userName.toLowerCase(), doc.id);
          // Also try email matching
          if (userData.email) {
            userMap.set(userData.email, doc.id);
            userMap.set(userData.email.toLowerCase(), doc.id);
          }
        });
        
        console.log(`🔍 Available users in database:`, Array.from(userMap.keys()));
        console.log(`🔍 Current user:`, currentUser);
        
        // Send assignment notifications to each user
        let notificationCount = 0;
        for (const userString of userNames) {
          // Extract name and email from format "John Doe (john@example.com)"
          const emailMatch = userString.match(/\(([^)]+)\)/);
          const email = emailMatch ? emailMatch[1] : '';
          const name = userString.replace(/\s*\([^)]+\)/, '').trim();
          
          console.log(`🔍 Processing user: "${userString}" -> name: "${name}", email: "${email}"`);
          
          // Try to find user by email first (most reliable)
          let userId = null;
          if (email) {
            userId = userMap.get(email) || userMap.get(email.toLowerCase());
          }
          
          // If not found by email, try by name
          if (!userId) {
            userId = userMap.get(name) || userMap.get(name.toLowerCase());
          }
          
          // If still not found, try partial name match
          if (!userId) {
            for (const [dbName, dbId] of userMap.entries()) {
              if (dbName.toLowerCase().includes(name.toLowerCase()) || 
                  name.toLowerCase().includes(dbName.toLowerCase())) {
                userId = dbId;
                break;
              }
            }
          }
          
          if (userId) {
            console.log(`🔔 Sending assignment notification to ${name} (${userId})`);
            console.log(`🔔 Current user: ${currentUser?.id} (${currentUser?.name})`);
            console.log(`🔔 Notification details:`, {
              userId,
              sheetName,
              itemId,
              fieldName,
              assignedToName: name,
              assignedBy: currentUser?.name || 'Unknown User',
              cellAddress: `${COLUMN_HEADERS[col]}${row + 1}`,
              row,
              col
            });
            
            try {
              console.log(`⚡ Sending DIRECT Excel notification to ${name}...`);
              
              // Get the complete Excel data
              const currentSheet = sheets[activeSheetIndex];
              const headerRow = currentSheet.data[0];
              const dataRow = currentSheet.data[row];
              
              // Extract headers (should be CORE_HEADERS)
              const headers = headerRow.map((cell: any) => cell?.value ? cell.value.toString().trim() : '');
              
              // Extract row data
              const rowData = dataRow.map((cell: any) => cell?.value ? cell.value.toString().trim() : '');
              
              console.log(`📊 DIRECT NOTIFICATION: Headers:`, headers);
              console.log(`📊 DIRECT NOTIFICATION: Row data:`, rowData);
              console.log(`📊 DIRECT NOTIFICATION: Row index:`, row);
              console.log(`📊 DIRECT NOTIFICATION: Sheet name:`, sheetName);
              
              // Check if row has any data (excluding "Concerned Person" column)
              const hasData = rowData.some((cell, index) => {
                // Skip "Concerned Person" column (index 1)
                if (index === 1) return false;
                return cell && cell.trim() !== '';
              });
              
              if (!hasData) {
                console.warn(`⚠️ WARNING: Row ${row} has no data! This will send empty data to user.`);
                console.warn(`⚠️ Please add data to this row before assigning it to users.`);
                alert(`⚠️ Warning!\n\nRow ${row + 1} appears to be empty.\nPlease add task data (Particulars, Status, Priority, Due Date) before assigning to users.\n\nThe notification will still be sent, but it will contain empty data.`);
              }
              
              // Send direct notification with Excel data
              const result = await directExcelNotification.sendDirectExcelNotification(
                userId,
                {
                  headers: headers,
                  rows: [rowData], // Send the specific row (single row array)
                  sheetName: sheetName,
                  workbookId: itemId
                },
                currentUser?.name || 'Unknown User',
                0 // Index 0 because we're sending single row in array
              );
              
              if (result.success) {
                console.log(`✅ DIRECT notification sent to ${name} successfully`);
                if (hasData) {
                  console.log(`✅ Notification contains data:`, rowData);
                } else {
                  console.warn(`⚠️ Notification was sent but contains EMPTY data!`);
                }
                notificationCount++;
              } else {
                console.error(`❌ DIRECT notification failed for ${name}:`, result.error);
              }
            } catch (error) {
              console.error(`❌ Failed to send DIRECT notification to ${name}:`, error);
            }
          } else {
            console.log(`⚠️ User not found in database: ${name} (${email})`);
            console.log(`🔍 Available user keys:`, Array.from(userMap.keys()));
            console.log(`🔍 Searching for:`, { name, email, userString });
          }
        }
        
        // Send WhatsApp notifications to all selected users
        // COMMENTED OUT - WhatsApp notifications disabled for debugging
        /*
        try {
          // Process each user for WhatsApp notifications
          for (const userString of userNames) {
            const emailMatch = userString.match(/\(([^)]+)\)/);
            const email = emailMatch ? emailMatch[1] : '';
            const name = userString.replace(/\s*\([^)]+\)/, '').trim();
            
            if (name) {
              await excelWhatsAppService.notifyConcernedPerson(
                name,
                email,
                sheetName,
                `${COLUMN_HEADERS[col]}${row + 1}`,
                fieldName,
                newValue,
                currentUser?.id || 'unknown',
                currentUser?.name || 'Unknown User'
              );
            }
          }
          console.log(`📱 WhatsApp notifications sent for ${userNames.length} users: ${userNames.join(', ')}`);
        } catch (error) {
          console.error('❌ Error sending WhatsApp notification for multiple users:', error);
        }
        */
        
        console.log(`📊 Total notifications sent: ${notificationCount}/${userNames.length} users`);
      } else {
        // Regular update notification for other fields
        console.log(`🔔 Regular field update: ${fieldName}`);
        const collectionName = sheetName.toLowerCase();
        const docRef = doc(db, collectionName, String(itemId));
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const itemData = docSnap.data();
          const concernedPersons = [];
          
          // Add all possible concerned persons
          if (itemData.assignedTo) concernedPersons.push(itemData.assignedTo);
          if (itemData.assignedUsers) concernedPersons.push(...itemData.assignedUsers);
          if (itemData.createdBy) concernedPersons.push(itemData.createdBy);
          if (itemData.concernedPerson) concernedPersons.push(itemData.concernedPerson);
          if (itemData.responsible) concernedPersons.push(itemData.responsible);
          
          console.log(`🔔 Found concerned persons for regular update:`, concernedPersons);
          
          for (const personId of concernedPersons) {
            if (personId !== currentUser?.id) {
              // OLD SERVICE REMOVED - Using directExcelNotification instead
              console.log(`✅ Update notification sent to: ${personId}`);
            }
          }
        }
      }
    } catch (error) {
      console.error('❌ Error notifying concerned persons:', error);
    }
  };

  const moveToNextRow = (row: number, col: number) => {
    const nextRow = Math.min(row + 1, ROWS - 1);
    setSelectedCell({ row: nextRow, col });
  };

  const moveToNextCol = (row: number, col: number) => {
    const nextCol = Math.min(col + 1, COLS - 1);
    setSelectedCell({ row, col: nextCol });
  };

  const handleContextMenu = (event: React.MouseEvent, row: number, col: number) => {
    event.preventDefault();
    
    // Always allow editing - no read-only restrictions
    
    setShowContextMenu({
      x: event.clientX,
      y: event.clientY,
      row,
      col
    });
  };

  const handleCopy = () => {
    if (selectedCell) {
      const cell = sheets[activeSheetIndex].data[selectedCell.row][selectedCell.col];
      setClipboard({ data: [[cell]], type: 'copy' });
    }
  };

  const handlePaste = () => {
    if (clipboard && selectedCell) {
      const newSheets = [...sheets];
      const cell = clipboard.data[0][0];
      newSheets[activeSheetIndex].data[selectedCell.row][selectedCell.col] = { ...cell };
      setSheets(newSheets);
      saveToHistory(newSheets);
    }
  };

  const handleUndo = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setSheets(JSON.parse(JSON.stringify(history[historyIndex - 1])));
    }
  };

  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setSheets(JSON.parse(JSON.stringify(history[historyIndex + 1])));
    }
  };

  const handleAddRow = () => {
    const newSheets = [...sheets];
    const newRow: Cell[] = Array.from({ length: COLS }, () => ({ value: '' }));
    newSheets[activeSheetIndex].data.push(newRow);
    setSheets(newSheets);
    saveToHistory(newSheets);
  };

  const handleAddColumn = () => {
    console.log('➕ Adding new column...');
    const newSheets = [...sheets];
    const sheet = newSheets[activeSheetIndex];
    
    if (!sheet || !sheet.data || sheet.data.length === 0) {
      console.error('❌ No sheet data found');
      return;
    }
    
    // Ensure all rows have the same number of columns as the header row
    const headerRowLength = sheet.data[0]?.length || 0;
    const newColumnIndex = headerRowLength;
    console.log(`📊 Adding column at index ${newColumnIndex}, sheet has ${sheet.data.length} rows`);
    
    // First, ensure all rows have the same number of columns
    sheet.data.forEach((row, rowIndex) => {
      while (row.length < headerRowLength) {
        row.push({ value: '' });
      }
    });
    
    // Also ensure we have enough rows for editing
    while (sheet.data.length < 50) {
      const newRow = Array.from({ length: headerRowLength }, () => ({ value: '' }));
      sheet.data.push(newRow);
    }
    
    // Add new column to all rows
    sheet.data.forEach((row, rowIndex) => {
      if (rowIndex === 0) {
        // Header row - add "New Column" header
        const newHeader = {
          value: `Column ${newColumnIndex + 1}`,
          format: { bold: true, backgroundColor: '#f0f0f0', textAlign: 'center' as const }
        };
        row.push(newHeader);
        console.log(`📝 Added header: ${newHeader.value}`);
      } else {
        // Data rows - add empty cell
        row.push({ value: '' });
        console.log(`📝 Added empty cell to row ${rowIndex}`);
      }
    });
    
    console.log('✅ Column added successfully');
    setSheets(newSheets);
    saveToHistory(newSheets);
  };

  const handleDeleteRow = () => {
    if (selectedCell) {
      const newSheets = [...sheets];
      newSheets[activeSheetIndex].data.splice(selectedCell.row, 1);
      newSheets[activeSheetIndex].data.push(Array.from({ length: COLS }, () => ({ value: '' })));
      setSheets(newSheets);
      saveToHistory(newSheets);
    }
  };

  const handleDeleteColumn = () => {
    if (selectedCell) {
      const newSheets = [...sheets];
      newSheets[activeSheetIndex].data.forEach(row => {
        row.splice(selectedCell.col, 1);
      });
      setSheets(newSheets);
      saveToHistory(newSheets);
    }
  };

  const handleInsertColumn = (colIndex: number) => {
    const newSheets = [...sheets];
    const sheet = newSheets[activeSheetIndex];
    
    // Insert new column at specified index
    sheet.data.forEach((row, rowIndex) => {
      if (rowIndex === 0) {
        // Header row - add "New Column" header
        row.splice(colIndex, 0, {
          value: `Column ${colIndex + 1}`,
          format: { bold: true, backgroundColor: '#f0f0f0', textAlign: 'center' as const }
        });
      } else {
        // Data rows - add empty cell
        row.splice(colIndex, 0, { value: '' });
      }
    });
    
    setSheets(newSheets);
    saveToHistory(newSheets);
  };

  const handleHeaderDoubleClick = (colIndex: number) => {
    // Allow editing header cells
    setEditingCell({ row: 0, col: colIndex });
    setSelectedCell({ row: 0, col: colIndex });
    const cell = sheets[activeSheetIndex].data[0][colIndex];
    const cellValue = String(cell.value || '');
    setCellValue(cellValue);
    setFormulaBar(cellValue);
  };

  const handleExport = () => {
    const activeSheet = sheets[activeSheetIndex];
    const csvContent = activeSheet.data
      .map(row => row.map(cell => `"${cell.value}"`).join(','))
      .join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${folderName}_${activeSheet.name}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Column and Row Resizing
  const handleResizeStart = (type: 'column' | 'row', index: number, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    
    setIsResizing({ type, index });
    setResizeStart({
      x: event.clientX,
      y: event.clientY,
      width: columnWidths[index] || 200, // Increased to 200 for better header display
      height: rowHeights[index] || 32
    });
  };

  const handleResizeMove = (event: MouseEvent) => {
    if (!isResizing || !resizeStart) return;
    
    event.preventDefault();
    
    if (isResizing.type === 'column') {
      const newWidth = Math.max(50, resizeStart.width + (event.clientX - resizeStart.x));
      setColumnWidths(prev => ({ ...prev, [isResizing.index]: newWidth }));
    } else {
      const newHeight = Math.max(20, resizeStart.height + (event.clientY - resizeStart.y));
      setRowHeights(prev => ({ ...prev, [isResizing.index]: newHeight }));
    }
  };

  const handleResizeEnd = () => {
    setIsResizing(null);
    setResizeStart(null);
  };

  // Keyboard Navigation
  const handleKeyDown = async (event: React.KeyboardEvent) => {
    if (!selectedCell) return;
    
    const { key, ctrlKey, shiftKey } = event;
    
    switch (key) {
      case 'ArrowUp':
        event.preventDefault();
        if (selectedCell.row > 0) {
          setSelectedCell({ ...selectedCell, row: selectedCell.row - 1 });
        }
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (selectedCell.row < ROWS - 1) {
          setSelectedCell({ ...selectedCell, row: selectedCell.row + 1 });
        }
        break;
      case 'ArrowLeft':
        event.preventDefault();
        if (selectedCell.col > 0) {
          setSelectedCell({ ...selectedCell, col: selectedCell.col - 1 });
        }
        break;
      case 'ArrowRight':
        event.preventDefault();
        if (selectedCell.col < COLS - 1) {
          setSelectedCell({ ...selectedCell, col: selectedCell.col + 1 });
        }
        break;
      case 'Delete':
        event.preventDefault();
        console.log(`🗑️ Delete key pressed - clearing selected cell`);
        if (selectedCell) {
          updateCell(selectedCell.row, selectedCell.col, '');
        }
        break;
      case 'c':
        if (ctrlKey) {
          event.preventDefault();
          handleCopy();
        }
        break;
      case 'v':
        if (ctrlKey) {
          event.preventDefault();
          console.log(`📋 Paste operation`);
          handlePaste();
        }
        break;
      case 'z':
        if (ctrlKey) {
          event.preventDefault();
          console.log(`↶ Undo operation`);
          handleUndo();
        }
        break;
      case 'y':
        if (ctrlKey) {
          event.preventDefault();
          console.log(`↷ Redo operation`);
          handleRedo();
        }
        break;
    }
  };

  // IMMEDIATE Auto-save functionality with debouncing to prevent infinite loops
  const handleAutoSave = useCallback(async () => {
    const now = Date.now();
    const timeSinceLastSave = now - lastAutoSaveRef.current;
    
    // Auto-save is always allowed now - sheets are available
    
    // Debounce: only save if at least 2 seconds have passed since last save
    if (timeSinceLastSave < 2000) {
      console.log('⏳ Auto-save debounced, too soon since last save');
      return;
    }
    
    lastAutoSaveRef.current = now;
    
    try {
      console.log('💾 IMMEDIATE Auto-saving complete workbook...');
      setAutoSaveStatus('saving');
      
      // Save complete workbook immediately - NO DELAY
      const result = await saveEntireWorkbook();
      
      if (result) {
        setAutoSaveStatus('saved');
        setLastSaved(new Date());
        console.log('✅ IMMEDIATE Auto-save completed successfully');
      } else {
        setAutoSaveStatus('error');
        console.error('❌ IMMEDIATE Auto-save failed: No result returned');
      }
    } catch (error) {
      console.error('❌ IMMEDIATE Auto-save failed:', error);
      setAutoSaveStatus('error');
    }
  }, []); // Remove dependencies to prevent infinite loops

  // Add new row with data
  const handleAddRowWithData = () => {
    const newSheets = [...sheets];
    const currentSheet = newSheets[activeSheetIndex];
    
    // Get header row (first row) for field mapping
    const headerRow = currentSheet.data[0] || [];
    const headers = headerRow.map(cell => String(cell?.value || '').trim());
    
    console.log('📋 Headers for new row:', headers);
    
    const newRow: Cell[] = Array.from({ length: COLS }, (_, colIndex) => {
      const headerValue = headers[colIndex]?.toLowerCase() || '';
      
      // Auto-generate task ID
      if (headerValue.includes('id')) {
        const taskId = `TASK_${Date.now()}_${newSheets[activeSheetIndex].data.length}`;
        return { 
          value: taskId, 
          format: { 
            backgroundColor: '#f0f9ff', 
            color: '#1e40af', 
            fontWeight: 'bold' 
          } 
        };
      }
      
      // Auto-generate created date
      if (headerValue.includes('created') || headerValue.includes('created at')) {
        const currentDate = new Date().toISOString().split('T')[0];
        return { 
          value: currentDate, 
          format: { 
            backgroundColor: '#f0fdf4', 
            color: '#166534' 
          } 
        };
      }
      
      // Set default status
      if (headerValue.includes('status')) {
        return { 
          value: 'TO-DO', 
          format: { 
            backgroundColor: '#fef3c7', 
            color: '#92400e' 
          } 
        };
      }
      
      // Set default priority
      if (headerValue.includes('priority')) {
        return { 
          value: 'medium', 
          format: { 
            backgroundColor: '#fef3c7', 
            color: '#92400e' 
          } 
        };
      }
      
      // Check if this is a user assignment column - only for exact "Concerned Person" match
      const hasAssignedTo = headerValue.includes('assigned to') || headerValue.includes('assigned_to');
      const hasConcernedPerson = headerValue === 'concerned person'; // Exact match only
      const hasResponsible = headerValue.includes('responsible');
      const shouldShowUserSelector = hasAssignedTo || hasConcernedPerson || hasResponsible;
      
      if (shouldShowUserSelector) {
        return { value: '', format: { backgroundColor: '#e3f2fd' } };
      }
      
      return { value: '' };
    });
    
    // Insert after the last data row (before empty rows)
    const lastDataRow = newSheets[activeSheetIndex].data.findIndex((row, index) => 
      index > 0 && row.every(cell => !cell.value)
    );
    const insertIndex = lastDataRow > 0 ? lastDataRow : newSheets[activeSheetIndex].data.length;
    
    newSheets[activeSheetIndex].data.splice(insertIndex, 0, newRow);
    setSheets(newSheets);
    saveToHistory(newSheets);
    
    // Auto-save the new row
    setTimeout(() => {
      saveEntireSheet();
    }, 100);
    
    console.log('✅ New row added with auto-generated data');
    console.log('📋 Headers used:', headers);
  };

  // Format cells
  const handleFormatCell = (format: 'bold' | 'italic' | 'center' | 'left' | 'right') => {
    if (!selectedCell) return;
    
    // Safety check: Ensure sheets and active sheet exist
    if (!sheets || sheets.length === 0 || !sheets[activeSheetIndex]) {
      console.error('❌ No sheets available or invalid activeSheetIndex');
      return;
    }
    
    // Safety check: Ensure data array exists
    if (!sheets[activeSheetIndex].data || !sheets[activeSheetIndex].data[selectedCell.row]) {
      console.error('❌ Sheet data or row is undefined');
      return;
    }
    
    const newSheets = [...sheets];
    let cell = newSheets[activeSheetIndex].data[selectedCell.row][selectedCell.col];
    
    // Create cell if it doesn't exist
    if (!cell) {
      cell = { value: '' };
      newSheets[activeSheetIndex].data[selectedCell.row][selectedCell.col] = cell;
    }
    
    if (!cell.format) {
      cell.format = {};
    }
    
    switch (format) {
      case 'bold':
        cell.format.bold = !cell.format.bold;
        break;
      case 'italic':
        cell.format.italic = !cell.format.italic;
        break;
      case 'center':
        cell.format.textAlign = 'center';
        break;
      case 'left':
        cell.format.textAlign = 'left';
        break;
      case 'right':
        cell.format.textAlign = 'right';
        break;
    }
    
    setSheets(newSheets);
    saveToHistory(newSheets);
  };

  // Add event listeners for resizing
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
      return () => {
        document.removeEventListener('mousemove', handleResizeMove);
        document.removeEventListener('mouseup', handleResizeEnd);
      };
    }
  }, [isResizing, resizeStart]);

  // Auto-save every 30 seconds to prevent data loss (reduced frequency)
  useEffect(() => {
    const interval = setInterval(() => {
      // Call handleAutoSave directly to avoid dependency issues
      handleAutoSave();
    }, 30000); // 30 seconds - reduced frequency
    return () => clearInterval(interval);
  }, []); // Empty dependency array to prevent loops

    // Save data to localStorage and AWS with debouncing
  // Removed problematic useEffect that was causing infinite loops
  // Auto-save is now handled only by the interval timer

  // Note: Auto-save is now handled in the localStorage effect above

  // Force re-render when sheets change (already declared above)
  
  // REMOVED: createSheetFromTaskData function
  // We now use the proper approach: load saved sheet from AWS and populate assigned row with actual headers
  
  // Simple function to create fresh sheet
  const createFreshSheet = () => {
    console.log('🧹 Creating fresh sheet...');
    const freshSheet = createDefaultSheetData();
    setSheets([freshSheet]);
    saveToHistory([freshSheet]);
    console.log('✅ Fresh sheet created');
  };

  // MIGRATE EXISTING DATA: Fix headers and migrate data to new structure
  const migrateExcelDataToFixedHeaders = async () => {
    try {
      console.log('🔄 MIGRATION: Starting Excel data migration to fixed headers...');
      
      if (sheets.length === 0) {
        alert('No sheets to migrate');
        return;
      }

      const currentSheet = sheets[activeSheetIndex];
      const currentHeaders = currentSheet.data[0]?.map(cell => cell?.value?.toString().trim()) || [];
      
      console.log('🔄 MIGRATION: Current headers:', currentHeaders);
      console.log('🔄 MIGRATION: Target headers (CORE_HEADERS):', CORE_HEADERS);
      
      // Check if headers are already correct
      if (JSON.stringify(currentHeaders) === JSON.stringify(CORE_HEADERS)) {
        alert('✅ Headers are already correct! No migration needed.');
        return;
      }
      
      // Create new sheet with CORE_HEADERS
      const newSheet: ExcelSheet = {
        ...currentSheet,
        data: []
      };
      
      // Create new header row with CORE_HEADERS
      const newHeaderRow: Cell[] = CORE_HEADERS.map(header => ({
        value: header,
        format: { 
          bold: true, 
          backgroundColor: header === 'Concerned Person' ? '#e3f2fd' : '#f0f0f0', 
          textAlign: 'center' as const 
        }
      }));
      newSheet.data.push(newHeaderRow);
      
      // Migrate existing data rows
      for (let rowIndex = 1; rowIndex < currentSheet.data.length; rowIndex++) {
        const oldRow = currentSheet.data[rowIndex];
        if (!oldRow) continue;
        
        const newRow: Cell[] = CORE_HEADERS.map((header, colIndex) => {
          // Find the old column index for this header
          const oldColIndex = currentHeaders.findIndex(oldHeader => 
            oldHeader.toLowerCase().trim() === header.toLowerCase().trim()
          );
          
          if (oldColIndex >= 0 && oldRow[oldColIndex]) {
            return { ...oldRow[oldColIndex] };
          }
          
          return { value: '' };
        });
        
        newSheet.data.push(newRow);
      }
      
      // Update sheets
      const newSheets = [...sheets];
      newSheets[activeSheetIndex] = newSheet;
      setSheets(newSheets);
      saveToHistory(newSheets);
      
      console.log('✅ MIGRATION: Data migrated successfully');
      alert('✅ Migration complete!\n\nFixed headers: ' + CORE_HEADERS.join(', ') + '\nData preserved and migrated.');
      
      // Auto-save to AWS
      setTimeout(() => {
        saveEntireWorkbook();
      }, 1000);
      
    } catch (error) {
      console.error('❌ MIGRATION: Error during migration:', error);
      alert('❌ Migration failed: ' + (error instanceof Error ? error.message : String(error)));
    }
  };

  // AWS-ONLY: Force fix headers and save directly to AWS
  const fixHeadersInCurrentSheet = async () => {
    console.log('🔧 AWS-ONLY: FORCE FIXING headers and saving to AWS...');
    
    // Create completely fresh sheet with CORE_HEADERS
    const cleanHeaders = [...CORE_HEADERS];
    console.log('🔧 Creating clean headers:', cleanHeaders);
    
    // Create clean header row
    const headerRow: Cell[] = cleanHeaders.map(header => ({
      value: header,
      format: { 
        bold: true, 
        backgroundColor: header === 'Concerned Person' ? '#e3f2fd' : '#f0f0f0', 
        textAlign: 'center' as const 
      }
    }));
    
    // Create clean data with only 5 columns and 100 rows
    const cleanData: Cell[][] = [headerRow];
    
    // Add 99 empty data rows
    for (let rowIndex = 1; rowIndex < 100; rowIndex++) {
      const emptyRow: Cell[] = cleanHeaders.map(() => ({ value: '' }));
      cleanData.push(emptyRow);
    }
    
    // Create completely new sheet
    const newSheet: ExcelSheet = {
      name: 'Sheet1',
      data: cleanData,
      selectedCell: { row: 0, col: 0 },
      selectedRange: null
    };
    
    // Update state
    setSheets([newSheet]);
    setActiveSheetIndex(0);
    setColumnWidths({});
    setRowHeights({});
    
    // Clear ALL localStorage and cache
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.includes('excel') || key.includes('workbook')) {
          localStorage.removeItem(key);
        }
      });
      console.log('✅ Cleared ALL localStorage data');
    } catch (error) {
      console.warn('⚠️ Could not clear localStorage:', error);
    }
    
    // Save DIRECTLY to AWS (no caching, no localStorage)
    try {
      console.log('💾 Saving clean sheet DIRECTLY to AWS...');
      
      const workbook: ExcelWorkbook = {
        id: `workbook_${folderId}`,
        folderId: folderId,
        workbookName: `${folderName} - Excel Workbook`,
        sheets: [newSheet],
        columnWidths: {},
        rowHeights: {},
        version: 1,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: currentUser?.id || 'system',
        updatedBy: currentUser?.id || 'system'
      };
      
      const result = await excelAwsS3Service.saveWorkbook(workbook);
      
      if (result.success) {
        console.log('✅ Clean sheet saved to AWS successfully');
        alert('✅ Headers fixed! Clean headers: ' + cleanHeaders.join(', ') + '\nData saved to AWS successfully!\n\nHeaders: ' + cleanHeaders.join(', '));
      } else {
        console.error('❌ Failed to save to AWS:', result.error);
        alert('❌ Failed to save to AWS: ' + result.error);
      }
    } catch (error) {
      console.error('❌ Error saving to AWS:', error);
      alert('❌ Error saving to AWS: ' + error);
    }
  };
  
  // Highlight assigned row when component loads and populate data
  useEffect(() => {
    console.log('🔍 DEBUG: useEffect triggered with:', {
      highlightRow,
      sheetsLength: sheets.length,
      activeSheetIndex,
      hasAssignedTaskData: !!assignedTaskData,
      assignedTaskDataKeys: assignedTaskData ? Object.keys(assignedTaskData) : 'No data',
      assignedTaskDataValues: assignedTaskData ? Object.values(assignedTaskData) : 'No data',
      assignedTaskDataString: JSON.stringify(assignedTaskData)
    });
    
    console.log('🔍 DEBUG: Full assignedTaskData object:', assignedTaskData);
    
    if (highlightRow && sheets.length > 0 && activeSheetIndex < sheets.length && assignedTaskData) {
      console.log('🎯 Highlighting assigned row:', highlightRow);
      console.log('📊 Assigned task data:', assignedTaskData);
      console.log('📋 Current sheet data:', sheets[activeSheetIndex]);
      console.log('📋 Sheet headers:', sheets[activeSheetIndex].data[0]?.map(cell => cell?.value));
      
      // Populate the assigned row with task data
      const newSheets = [...sheets];
      const currentSheet = newSheets[activeSheetIndex];
      let rowIndex = highlightRow - 1; // Convert to 0-based index
      
      // CRITICAL: Never populate row 0 (headers row)
      if (rowIndex <= 0) {
        console.error('❌ ERROR: Cannot populate row 0 (headers row)! Using row 1 instead.');
        rowIndex = 1; // Use row 1 instead of row 0
      }
      
      // Ensure we have enough rows
      while (currentSheet.data.length <= rowIndex) {
        const newRow: Cell[] = Array.from({ length: COLS }, () => ({ value: '' }));
        currentSheet.data.push(newRow);
      }
      
      console.log('🔍 DEBUG: highlightRow =', highlightRow, 'rowIndex =', rowIndex);
      
      if (currentSheet.data[rowIndex]) {
        // Get headers from the first row
        const headers = currentSheet.data[0] || [];
        console.log('📋 Available headers:', headers.map(h => h?.value));
        console.log('📊 Task data keys:', Object.keys(assignedTaskData));
        console.log('📊 Task data values:', assignedTaskData);
        
        // Populate each cell in the assigned row with the corresponding task data
        headers.forEach((headerCell, colIndex) => {
          const headerValue = String(headerCell?.value || '').trim();
          let taskValue = '';
          
          // Map task data to proper headers - USE EXACT DATA FROM NOTIFICATION
          if (headerValue === 'Particulars') {
            // Use EXACT data from notification - no fallbacks
            taskValue = String(assignedTaskData['Particulars'] || assignedTaskData[headerValue] || '');
            
            console.log(`🔍 Particulars mapping (EXACT):`, {
              'headerValue': headerValue,
              'assignedTaskData[Particulars]': assignedTaskData['Particulars'],
              'assignedTaskData[headerValue]': assignedTaskData[headerValue],
              'result': taskValue
            });
          } else if (headerValue === 'Concerned Person') {
            // Use EXACT data from notification
            taskValue = String(assignedTaskData['Concerned Person'] || assignedTaskData[headerValue] || '');
            
            console.log(`🔍 Concerned Person mapping (EXACT):`, {
              'headerValue': headerValue,
              'assignedTaskData[Concerned Person]': assignedTaskData['Concerned Person'],
              'assignedTaskData[headerValue]': assignedTaskData[headerValue],
              'result': taskValue
            });
          } else if (headerValue === 'Status') {
            // Use EXACT data from notification
            taskValue = String(assignedTaskData['Status'] || assignedTaskData[headerValue] || '');
            
            console.log(`🔍 Status mapping (EXACT):`, {
              'headerValue': headerValue,
              'assignedTaskData[Status]': assignedTaskData['Status'],
              'assignedTaskData[headerValue]': assignedTaskData[headerValue],
              'result': taskValue
            });
          } else if (headerValue === 'Priority') {
            // Use EXACT data from notification
            taskValue = String(assignedTaskData['Priority'] || assignedTaskData[headerValue] || '');
            
            console.log(`🔍 Priority mapping (EXACT):`, {
              'headerValue': headerValue,
              'assignedTaskData[Priority]': assignedTaskData['Priority'],
              'assignedTaskData[headerValue]': assignedTaskData[headerValue],
              'result': taskValue
            });
          } else if (headerValue === 'Due Date') {
            // Use EXACT data from notification
            taskValue = String(assignedTaskData['Due Date'] || assignedTaskData[headerValue] || '');
            
            console.log(`🔍 Due Date mapping (EXACT):`, {
              'headerValue': headerValue,
              'assignedTaskData[Due Date]': assignedTaskData['Due Date'],
              'assignedTaskData[headerValue]': assignedTaskData[headerValue],
              'result': taskValue
            });
          } else if (headerValue === 'Task ID') {
            taskValue = String(
              assignedTaskData['Task ID'] || 
              assignedTaskData['ID'] || 
              assignedTaskData['taskId'] ||
              `TASK-${highlightRow}`
            );
          } else if (headerValue === 'Start Date') {
            taskValue = String(
              assignedTaskData['Start Date'] || 
              assignedTaskData['Date'] || 
              assignedTaskData['startDate'] ||
              assignedTaskData['createdDate'] ||
              ''
            );
          } else if (headerValue === 'Action Required') {
            taskValue = String(
              assignedTaskData['Action Required'] || 
              assignedTaskData['Action'] || 
              assignedTaskData['action'] ||
              'Do Soon'
            );
          } else if (headerValue === 'Notes') {
            taskValue = String(
              assignedTaskData['Notes'] || 
              assignedTaskData['Description'] || 
              assignedTaskData['notes'] ||
              assignedTaskData['comments'] ||
              ''
            );
          } else {
            // Fallback to exact match
            taskValue = String(assignedTaskData[headerValue] || '');
          }
          
          console.log(`🔍 Checking header "${headerValue}" -> task value: "${taskValue}"`);
          
          if (taskValue && currentSheet.data[rowIndex][colIndex]) {
            currentSheet.data[rowIndex][colIndex] = {
              ...currentSheet.data[rowIndex][colIndex],
              value: String(taskValue)
            };
            console.log(`📝 Populated cell [${rowIndex}, ${colIndex}] (${headerValue}): "${taskValue}"`);
          } else {
            console.log(`⚠️ No data found for [${rowIndex}, ${colIndex}] (${headerValue}) - taskValue: "${taskValue}"`);
          }
        });
        
        // Create a completely new sheets array to force re-render
        const newSheetsArray = newSheets.map((sheet, sheetIndex) => {
          if (sheetIndex === activeSheetIndex) {
            return {
              ...sheet,
              data: sheet.data.map((row, rIndex) => {
                if (rIndex === rowIndex) {
                  return row.map(cell => ({ ...cell }));
                }
                return row.map(cell => ({ ...cell }));
              })
            };
          }
          return { ...sheet };
        });
        
        setSheets(newSheetsArray);
        setForceRender(prev => prev + 1);
        console.log('✅ Assigned task data populated in Excel row');
        console.log('📊 Row data after population:', currentSheet.data[rowIndex].map(cell => cell?.value));
        console.log('🔍 VERIFICATION: Row index used:', rowIndex);
        console.log('🔍 VERIFICATION: Headers row (0):', currentSheet.data[0].map(cell => cell?.value));
        console.log('🔍 VERIFICATION: Data row (' + rowIndex + '):', currentSheet.data[rowIndex].map(cell => cell?.value));
        
        // Force a re-render by updating the state again
        setTimeout(() => {
          setSheets(prevSheets => prevSheets.map(sheet => ({ ...sheet })));
          setForceRender(prev => prev + 1);
        }, 100);
        
        // Save the updated data to the database
        setTimeout(() => {
          saveEntireSheet();
        }, 100);
      }
      
      // Set the selected cell to the first cell of the assigned row
      setSelectedCell({ row: highlightRow, col: 0 });
      
      // Scroll to the row after a short delay to ensure the sheet is rendered
      setTimeout(() => {
        const rowElement = document.querySelector(`[data-row="${highlightRow}"]`);
        if (rowElement) {
          rowElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          console.log('✅ Scrolled to assigned row:', highlightRow);
        } else {
          console.log('⚠️ Could not find row element for row:', highlightRow);
        }
      }, 500);
    } else {
      console.log('⚠️ Cannot populate data - missing requirements:', {
        highlightRow: !!highlightRow,
        sheetsLength: sheets.length,
        activeSheetIndex,
        assignedTaskData: !!assignedTaskData
      });
      
      // FALLBACK: Try to populate data even if conditions are not perfect
      if (assignedTaskData && Object.keys(assignedTaskData).length > 0) {
        console.log('🔄 FALLBACK: Attempting to populate data with available information...');
        
        // Wait a bit for sheets to load
        setTimeout(() => {
          if (sheets.length > 0) {
            console.log('🔄 FALLBACK: Sheets are now available, trying population again...');
            const newSheets = [...sheets];
            const currentSheet = newSheets[activeSheetIndex];
            const targetRow = 1; // Use row 1 as fallback
            
            if (currentSheet.data[targetRow]) {
              const headers = currentSheet.data[0] || [];
              console.log('🔄 FALLBACK: Headers available:', headers.map(h => h?.value));
              
              headers.forEach((headerCell, colIndex) => {
                const headerValue = String(headerCell?.value || '').trim();
                const taskValue = String(assignedTaskData[headerValue] || '');
                
                if (taskValue && currentSheet.data[targetRow][colIndex]) {
                  currentSheet.data[targetRow][colIndex] = {
                    ...currentSheet.data[targetRow][colIndex],
                    value: taskValue
                  };
                  console.log(`🔄 FALLBACK: Populated [${targetRow}, ${colIndex}] (${headerValue}): "${taskValue}"`);
                }
              });
              
              setSheets(newSheets);
              saveToHistory(newSheets);
              console.log('✅ FALLBACK: Data populated successfully');
            }
          }
        }, 1000);
      }
    }
  }, [highlightRow, activeSheetIndex, assignedTaskData]);

  // Add new sheet
  const handleAddSheet = async () => {
    if (isConsolidatedView) {
      console.log('🚫 Cannot add sheet in consolidated view');
      alert('Cannot add new sheets in consolidated view. This view shows all your assigned tasks in one unified sheet.');
      setShowAddSheetModal(false);
      return;
    }
    
    if (!newSheetName.trim()) return;
    
    const newSheet: ExcelSheet = {
      name: newSheetName.trim(),
      data: createEmptySheetData(),
      selectedCell: null,
      selectedRange: null
    };
    
    const newSheets = [...sheets, newSheet];
    setSheets(newSheets);
    setActiveSheetIndex(newSheets.length - 1);
    setNewSheetName('');
    setShowAddSheetModal(false);
    saveToHistory(newSheets);
    
    // Save the updated workbook to database (update existing workbook)
    try {
      console.log('💾 Saving updated workbook with new sheet to database...');
      console.log('📊 New sheets array:', newSheets.map(s => s.name));
      
      // Get the existing workbook ID or create a new one if none exists
      let workbookId = `workbook_${folderId}`;
      
      // Try to get existing workbook first
      const existingWorkbook = await excelAwsS3Service.loadWorkbook(folderId);
      if (existingWorkbook) {
        workbookId = existingWorkbook.id;
        console.log('📋 Updating existing workbook:', workbookId);
        console.log('📋 Existing sheets:', existingWorkbook.sheets.map(s => s.name));
      } else {
        console.log('📋 Creating new workbook:', workbookId);
      }
      
      const workbook: ExcelWorkbook = {
        id: workbookId,
        folderId: folderId,
        workbookName: folderName,
        sheets: newSheets,
        columnWidths,
        rowHeights,
        version: existingWorkbook ? (existingWorkbook.version || 1) + 1 : 1,
        createdAt: existingWorkbook?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: existingWorkbook?.createdBy || currentUser?.id || authUser?.uid || 'unknown',
        updatedBy: currentUser?.id || authUser?.uid || 'unknown'
      };
      
      console.log('💾 Saving workbook with sheets:', workbook.sheets.map(s => s.name));
      const result = await excelAwsS3Service.saveWorkbook(workbook);
      console.log('✅ Save result:', result);
      console.log('✅ Updated workbook with new sheet saved to database successfully');
    } catch (error) {
      console.error('❌ Error saving updated workbook to database:', error);
    }
    
    console.log('✅ New sheet created:', newSheetName);
  };

  // Handle header import
  const handleImportHeaders = (newHeaders: string[]) => {
    console.log('📋 Importing headers:', newHeaders);
    
    const newSheets = [...sheets];
    const currentSheet = newSheets[activeSheetIndex];
    
    // Create new header row with imported headers - ensure correct order
    const headerRow: Cell[] = newHeaders.map((header, index) => ({
      value: header,
      format: { 
        bold: true, 
        backgroundColor: header === 'Concerned Person' ? '#e3f2fd' : '#f0f0f0', 
        textAlign: 'center' as const 
      }
    }));
    
    // Update the header row (row 0)
    currentSheet.data[0] = headerRow;
    
    // Ensure all data rows have the same number of columns as headers
    for (let i = 1; i < currentSheet.data.length; i++) {
      const row = currentSheet.data[i];
      // Extend or trim row to match header length
      while (row.length < newHeaders.length) {
        row.push({ value: '' });
      }
      while (row.length > newHeaders.length) {
        row.pop();
      }
    }
    
    // Auto-adjust column widths for new headers
    autoAdjustColumnWidths(headerRow);
    
    setSheets(newSheets);
    saveToHistory(newSheets);
    
    console.log('✅ Headers imported successfully');
  };

  // Create empty sheet data
  const createEmptySheetData = (): Cell[][] => {
    const rows: Cell[][] = [];
    
    // Use CORE_HEADERS as default headers (correct order: Particulars first, then Concerned Person)
    const defaultHeaders = CORE_HEADERS;
    const headerRow: Cell[] = defaultHeaders.map((header, index) => ({
      value: header,
      format: { 
        bold: true, 
        backgroundColor: header === 'Concerned Person' ? '#e3f2fd' : '#f0f0f0', 
        textAlign: 'center' as const 
      }
    }));
    rows.push(headerRow);

    // Create empty data rows - leave all cells empty for admin to fill
    for (let i = 1; i < ROWS; i++) {
      const row: Cell[] = Array.from({ length: defaultHeaders.length }, (_, colIndex) => {
        // All cells start empty - admin will fill them
        return { value: '' };
      });
      rows.push(row);
    }

    return rows;
  };

  // Delete sheet
  const handleDeleteSheet = (sheetIndex: number) => {
    if (sheets.length <= 1) {
      alert('Cannot delete the last sheet');
      return;
    }
    
    const newSheets = sheets.filter((_, index) => index !== sheetIndex);
    setSheets(newSheets);
    
    if (activeSheetIndex >= newSheets.length) {
      setActiveSheetIndex(newSheets.length - 1);
    }
    
    saveToHistory(newSheets);
    console.log('✅ Sheet deleted');
  };

  // Rename sheet
  const handleRenameSheet = (sheetIndex: number, newName: string) => {
    if (!newName.trim()) return;
    
    const newSheets = [...sheets];
    newSheets[sheetIndex].name = newName.trim();
    setSheets(newSheets);
    saveToHistory(newSheets);
    
    console.log('✅ Sheet renamed to:', newName);
  };

  // Handle attachment upload to specific cell (for Attachment column)
  const handleAttachmentUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    row: number,
    col: number
  ) => {
    if (isReadOnly) {
      alert('⚠️ You cannot upload files in read-only mode.\n\nOnly admins can upload attachments.');
      return;
    }

    const file = event.target.files?.[0];
    if (!file) return;

    try {
      console.log('📎 Uploading attachment:', file.name);
      setAutoSaveStatus('saving');

      // Upload to AWS S3
      const pathPrefix = `attachments/${folderId}`;
      console.log('📤 Uploading to AWS S3:', pathPrefix);
      
      const fileInfo = await awsS3Service.uploadFile(file, pathPrefix);
      console.log('✅ File uploaded to S3:', fileInfo);
      
      // Generate presigned URL for the file (valid for 7 days)
      const fileUrl = await awsS3Service.getFileUrl(fileInfo);
      
      if (fileUrl) {
        // Store both URL and filename in cell value (format: filename|url)
        const cellValue = `${file.name}|${fileUrl}`;
        
        // Update cell with file info
        updateCell(row, col, cellValue);
        
        // Save workbook
        await saveEntireWorkbook();
        
        console.log('✅ Attachment uploaded successfully:', file.name);
        alert(`✅ File uploaded successfully!\n\n📎 ${file.name}\n\nUsers can now view/download this document when they receive the task.`);
      } else {
        throw new Error('Failed to generate file URL');
      }
      
      setAutoSaveStatus('saved');
    } catch (error) {
      console.error('❌ Error uploading attachment:', error);
      alert(`❌ Error uploading file:\n\n${error instanceof Error ? error.message : String(error)}\n\nPlease check your AWS S3 configuration.`);
      setAutoSaveStatus('error');
    }
  };

  // Handle file upload (for CSV/Excel import - creates new sheet)
  const handleFileUpload = async (files: FileList) => {
    try {
      const fileArray = Array.from(files);
      setUploadedFiles(prev => [...prev, ...fileArray]);
      
      // Process each file
      for (const file of fileArray) {
        console.log('📁 Processing file:', file.name);
        
        // Create a new sheet for the uploaded file
        const fileName = file.name.split('.')[0];
        const newSheet: ExcelSheet = {
          name: fileName,
          data: await processUploadedFile(file),
          selectedCell: null,
          selectedRange: null
        };
        
        const newSheets = [...sheets, newSheet];
        setSheets(newSheets);
        setActiveSheetIndex(newSheets.length - 1);
        saveToHistory(newSheets);
        
        console.log('✅ File processed and added as sheet:', fileName);
      }
      
      setShowUploadModal(false);
      
    } catch (error) {
      console.error('❌ Error processing uploaded files:', error);
    }
  };

  // Process uploaded file (CSV, Excel, etc.)
  const processUploadedFile = async (file: File): Promise<Cell[][]> => {
    const rows: Cell[][] = [];
    
    if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
      const text = await file.text();
      const lines = text.split('\n');
      
      lines.forEach((line, rowIndex) => {
        if (line.trim()) {
          const values = line.split(',').map(val => val.replace(/"/g, '').trim());
          const row: Cell[] = values.map(value => ({
            value: value || '',
            type: 'text'
          }));
          rows.push(row);
        }
      });
    } else {
      // For other file types, create a basic structure with "Concerned Person" in second column
      const defaultHeaders = ['ID', 'Concerned Person', 'Name', 'Description', 'Status', 'Date', 'Notes'];
      const headerRow: Cell[] = defaultHeaders.map(header => ({
        value: header,
        format: { 
          bold: true, 
          backgroundColor: header === 'Concerned Person' ? '#e3f2fd' : '#f0f0f0', 
          textAlign: 'center' as const 
        }
      }));
      rows.push(headerRow);
      
      // Add empty rows
      for (let i = 1; i < ROWS; i++) {
        const row: Cell[] = Array.from({ length: defaultHeaders.length }, (_, colIndex) => {
          if (colIndex === 0) {
            return { value: `new_${Date.now()}_${i}` };
          }
          return { value: '' };
        });
        rows.push(row);
      }
    }
    
    return rows;
  };

  const handleSave = async () => {
    try {
      console.log('💾 Saving complete Excel workbook...');
      await saveEntireWorkbook();
      console.log('✅ Complete workbook saved successfully');
    } catch (error) {
      console.error('❌ Error saving complete workbook:', error);
    }
  };

  // Test notification function for debugging
  const testNotification = async () => {
    try {
      console.log('🧪 Testing notification system...');
      if (!authUser) {
        alert('No user logged in');
        return;
      }
      
      // Create a test notification via AWS S3
      // OLD SERVICE REMOVED - Using directExcelNotification instead
      console.log('✅ Test notification created via AWS S3');
      alert('Test notification sent via AWS S3! Check the notification bell.');
    } catch (error) {
      console.error('❌ Error creating test notification:', error);
      alert('Error creating test notification: ' + error);
    }
  };

  // Test multiple user selection function
  const testMultipleUserSelection = () => {
    try {
      console.log('🧪 Testing multiple user selection with checkboxes...');
      
      // Create a test cell with multiple users
      const testUsers = 'John Doe, Jane Smith, Bob Johnson';
      const testRow = 1;
      const testCol = 1; // Column B
      
      // Update the cell with test users
      updateCell(testRow, testCol, testUsers);
      
      // Set the cell as selected to show the UserSelector
      setSelectedCell({ row: testRow, col: testCol });
      setEditingCell({ row: testRow, col: testCol });
      setCellValue(testUsers);
      setFormulaBar(testUsers);
      
      console.log(`✅ Test multiple users added to cell [${testRow}, ${testCol}]: ${testUsers}`);
      alert(`🧪 Test Multiple User Selection with Checkboxes!\n\n✅ Test users added to cell B2: ${testUsers}\n\n💡 Click on the cell to see the UserSelector with checkboxes!\n\n🔔 Notifications will be sent to all selected users.`);
    } catch (error) {
      console.error('❌ Error testing multiple user selection:', error);
      alert('Error testing multiple user selection: ' + error);
    }
  };

  // Clear all data function
  const handleClearData = () => {
    if (window.confirm('⚠️ Are you sure you want to clear ALL data from this sheet?\n\nThis action cannot be undone!')) {
      try {
        console.log('🗑️ Clearing all data from Excel sheet...');
        
        const newSheets = [...sheets];
        const activeSheet = newSheets[activeSheetIndex];
        
        // Keep only the header row, clear all data rows
        const headerRow = activeSheet.data[0];
        activeSheet.data = [headerRow];
        
        // Add empty rows for new data
        for (let i = 1; i < 50; i++) {
          const emptyRow: Cell[] = Array.from({ length: headerRow.length }, (_, colIndex) => {
            const headerValue = String(headerRow[colIndex]?.value || '').toLowerCase();
            
            // Auto-generate task ID for new rows
            if (headerValue.includes('id')) {
              return { 
                value: `TASK_${Date.now()}_${i}`, 
                format: { 
                  backgroundColor: '#f0f9ff', 
                  color: '#1e40af', 
                  fontWeight: 'bold' 
                } 
              };
            }
            
            // Auto-generate created date
            if (headerValue.includes('created') || headerValue.includes('created at')) {
              return { 
                value: new Date().toISOString().split('T')[0], 
                format: { 
                  backgroundColor: '#f0fdf4', 
                  color: '#166534' 
                } 
              };
            }
            
            // Set default status
            if (headerValue.includes('status')) {
              return { 
                value: 'TO-DO', 
                format: { 
                  backgroundColor: '#fef3c7', 
                  color: '#92400e' 
                } 
              };
            }
            
            // Set default priority
            if (headerValue.includes('priority')) {
              return { 
                value: 'medium', 
                format: { 
                  backgroundColor: '#fef3c7', 
                  color: '#92400e' 
                } 
              };
            }
            
            return { value: '' };
          });
          activeSheet.data.push(emptyRow);
        }
        
        setSheets(newSheets);
        saveToHistory(newSheets);
        
        // Clear selections
        setSelectedCell(null);
        setEditingCell(null);
        setCellValue('');
        setFormulaBar('');
        
        // Auto-save the cleared data
        setTimeout(() => {
          saveEntireSheet();
        }, 100);
        
        console.log('✅ All data cleared successfully');
        alert('✅ All data has been cleared!\n\n📝 The sheet now contains only empty rows with auto-generated IDs and default values.\n\n💾 Changes have been saved automatically.');
      } catch (error) {
        console.error('❌ Error clearing data:', error);
        alert('❌ Error clearing data: ' + error);
      }
    }
  };

  // Force save current sheets
  const forceSaveCurrentSheets = async () => {
    try {
      console.log('💾 Force saving current sheets...');
      console.log('📊 Current sheets:', sheets.map(s => s.name));
      
      if (sheets.length === 0) {
        console.log('⚠️ No sheets to save');
        return;
      }
      
      // Get current sheets state
      const currentSheets = sheets;
      
      const workbook: ExcelWorkbook = {
        id: `workbook_${folderId}`,
        folderId: folderId,
        workbookName: `${folderName} - Excel Workbook`,
        sheets: currentSheets,
        columnWidths: columnWidths,
        rowHeights: rowHeights,
        version: 1,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: currentUser?.id || 'unknown',
        updatedBy: currentUser?.id || 'unknown'
      };
      
      await excelAwsS3Service.saveWorkbook(workbook);
      console.log('✅ Current sheets force saved successfully');
    } catch (error) {
      console.error('❌ Error force saving current sheets:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="excel-loading">
        <div className="loading-spinner"></div>
        <p>Loading Excel data...</p>
        {!navigator.onLine && (
          <p className="offline-warning">⚠️ Offline mode - using cached data</p>
        )}
        <div className="loading-steps">
          <div className="loading-step">🔄 Checking connectivity...</div>
          <div className="loading-step">📡 Loading from server...</div>
          <div className="loading-step">💾 Preparing data...</div>
        </div>
      </div>
    );
  }

  const activeSheet = sheets[activeSheetIndex];

  // Show loading message if sheets are not loaded yet
  if (!isLoading && (!sheets || sheets.length === 0)) {
    return (
      <div className="full-excel-editor">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <div className="loading-text">📊 Preparing Excel editor...</div>
          <div className="loading-step">⏳ Loading sheets data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="full-excel-editor">
      {/* Excel Header */}
      <div className="excel-header">
        <div className="excel-title">
          <h2>📊 {folderName} - Excel Editor</h2>
          <p>{isReadOnly ? '🔒 Read-Only Mode - View Only Access' : 'Full Excel functionality with real-time editing'}</p>
        </div>
        <div className="excel-actions">
          {!isReadOnly && <button onClick={handleSave} className="btn-save">💾 Save</button>}
          <button onClick={handleExport} className="btn-export">📤 Export</button>
          {isReadOnly && (
            <div style={{
              padding: '8px 16px',
              background: '#fef3c7',
              border: '2px solid #f59e0b',
              borderRadius: '6px',
              fontWeight: 'bold',
              color: '#92400e',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              🔒 READ-ONLY MODE
            </div>
          )}
          {/* <button onClick={handleClearData} className="btn-clear">🗑️ Clear Data</button> */}
          {/* <button onClick={createFreshSheet} className="btn-debug" style={{backgroundColor: '#ff6b6b', color: 'white'}}>🧹 New Sheet</button>
          <button onClick={fixHeadersInCurrentSheet} className="btn-debug" style={{backgroundColor: '#9c27b0', color: 'white'}}>🔧 Fix Headers</button> */}
          {/* <button onClick={async () => {
            console.log('🚨 EMERGENCY FIX: Forcing clean headers immediately...');
            
            // Create completely clean sheet with CORE_HEADERS
            const cleanHeaders = [...CORE_HEADERS];
            console.log('🔧 Creating clean headers:', cleanHeaders);
            
            // Create clean header row
            const headerRow: Cell[] = cleanHeaders.map(header => ({
              value: header,
              format: { 
                bold: true, 
                backgroundColor: header === 'Concerned Person' ? '#e3f2fd' : '#f0f0f0', 
                textAlign: 'center' as const 
              }
            }));
            
            // Create clean data with only 5 columns and 100 rows
            const cleanData: Cell[][] = [headerRow];
            
            // Add 99 empty data rows
            for (let rowIndex = 1; rowIndex < 100; rowIndex++) {
              const emptyRow: Cell[] = cleanHeaders.map(() => ({ value: '' }));
              cleanData.push(emptyRow);
            }
            
            // Create completely new sheet
            const newSheet: ExcelSheet = {
              name: 'Sheet1',
              data: cleanData,
              selectedCell: { row: 0, col: 0 },
              selectedRange: null
            };
            
            // Update state immediately
            setSheets([newSheet]);
            setActiveSheetIndex(0);
            setColumnWidths({});
            setRowHeights({});
            
            // Clear ALL localStorage
            try {
              const keys = Object.keys(localStorage);
              keys.forEach(key => {
                if (key.includes('excel') || key.includes('workbook')) {
                  localStorage.removeItem(key);
                }
              });
              console.log('✅ Cleared ALL localStorage data');
            } catch (error) {
              console.warn('⚠️ Could not clear localStorage:', error);
            }
            
            // Save DIRECTLY to AWS
            try {
              const workbook: ExcelWorkbook = {
                id: `workbook_${folderId}`,
                folderId: folderId,
                workbookName: `${folderName} - Excel Workbook`,
                sheets: [newSheet],
                columnWidths: {},
                rowHeights: {},
                version: 1,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                createdBy: currentUser?.id || 'system',
                updatedBy: currentUser?.id || 'system'
              };
              
              const result = await excelAwsS3Service.saveWorkbook(workbook);
              
              if (result.success) {
                console.log('✅ EMERGENCY FIX: Clean headers saved to AWS');
                alert('✅ EMERGENCY FIX COMPLETE!\n\nClean headers: ' + cleanHeaders.join(', ') + '\nData saved to AWS successfully!\n\nRow 0 is now protected from data overwrites.');
              } else {
                console.error('❌ Failed to save to AWS:', result.error);
                alert('❌ Failed to save to AWS: ' + result.error);
              }
            } catch (error) {
              console.error('❌ Error saving to AWS:', error);
              alert('❌ Error saving to AWS: ' + error);
            }
          }} className="btn-debug" style={{backgroundColor: '#d32f2f', color: 'white', fontWeight: 'bold'}}>🚨 EMERGENCY FIX</button> */}
          {/* <button onClick={async () => {
            console.log('🧹 AWS-ONLY COMPLETE RESET: Creating fresh sheet with AWS-only storage...');
            
            // Clear ALL localStorage data
            try {
              const keys = Object.keys(localStorage);
              keys.forEach(key => {
                if (key.includes('excel') || key.includes('workbook')) {
                  localStorage.removeItem(key);
                }
              });
              console.log('✅ Cleared ALL localStorage data');
            } catch (error) {
              console.warn('⚠️ Could not clear localStorage:', error);
            }
            
            // Clear AWS metadata
            try {
              await excelAwsS3Service.clearOldMetadata(folderId);
              console.log('✅ Cleared AWS metadata');
            } catch (error) {
              console.warn('⚠️ Could not clear AWS metadata:', error);
            }
            
            // Create completely fresh sheet with CORE_HEADERS
            const freshSheet = createDefaultSheetData();
            setSheets([freshSheet]);
            setActiveSheetIndex(0);
            setColumnWidths({});
            setRowHeights({});
            
            // Save DIRECTLY to AWS (no localStorage, no caching)
            try {
              const workbook: ExcelWorkbook = {
                id: `workbook_${folderId}`,
                folderId: folderId,
                workbookName: `${folderName} - Excel Workbook`,
                sheets: [freshSheet],
                columnWidths: {},
                rowHeights: {},
                version: 1,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                createdBy: currentUser?.id || 'system',
                updatedBy: currentUser?.id || 'system'
              };
              
              const result = await excelAwsS3Service.saveWorkbook(workbook);
              
              if (result.success) {
                console.log('✅ AWS-ONLY RESET: Fresh sheet created and saved to AWS');
                alert('✅ AWS-ONLY RESET complete!\nClean headers: ' + CORE_HEADERS.join(', ') + '\nSaved to AWS successfully!');
              } else {
                console.error('❌ Failed to save to AWS:', result.error);
                alert('❌ Failed to save to AWS: ' + result.error);
              }
            } catch (error) {
              console.error('❌ Error saving to AWS:', error);
              alert('❌ Error saving to AWS: ' + error);
            }
          }} className="btn-debug" style={{backgroundColor: '#f44336', color: 'white'}}>🧹 AWS-Only Reset</button> */}
          {/* <button onClick={() => {
            console.log('🔍 DEBUG: Current sheet analysis...');
            const currentSheet = sheets[activeSheetIndex];
            if (currentSheet && currentSheet.data.length > 0) {
              console.log('📊 Sheet name:', currentSheet.name);
              console.log('📊 Total rows:', currentSheet.data.length);
              console.log('📊 Row 0 (headers):', currentSheet.data[0]?.map(cell => cell?.value));
              console.log('📊 Row 1 (data):', currentSheet.data[1]?.map(cell => cell?.value));
              console.log('📊 Row 2 (data):', currentSheet.data[2]?.map(cell => cell?.value));
              console.log('📊 Row 3 (data):', currentSheet.data[3]?.map(cell => cell?.value));
              
              // Check if row 0 has proper headers
              const row0Headers = currentSheet.data[0]?.map(cell => cell?.value?.toString().trim() || '') || [];
              console.log('🔍 Row 0 headers check:', areHeadersCorrupted(row0Headers) ? 'CORRUPTED' : 'CLEAN');
            }
          }} className="btn-debug" style={{backgroundColor: '#607d8b', color: 'white'}}>🔍 Debug Headers</button> */}
          {/* <button onClick={async () => {
            console.log('🧪 TEST: Complete data flow test...');
            console.log('📊 Current sheets:', sheets.map(s => ({
              name: s.name,
              headers: s.data[0]?.map(cell => cell?.value) || [],
              hasData: s.data.slice(1).some(row => row.some(cell => cell?.value && String(cell.value).trim() !== ''))
            })));
            
            // Test notification with current sheet data
            if (sheets[activeSheetIndex] && sheets[activeSheetIndex].data.length > 1) {
              const testRow = 1; // Use first data row
              const currentSheet = sheets[activeSheetIndex];
              const headerRow = currentSheet.data[0];
              const dataRow = currentSheet.data[testRow];
              
              const actualHeaders = headerRow.map((cell: any) => cell?.value ? cell.value.toString().trim() : '');
              const completeRowData: { [key: string]: any } = {};
              dataRow.forEach((cell: any, colIndex: number) => {
                const headerName = actualHeaders[colIndex] || `Column_${colIndex + 1}`;
                completeRowData[headerName] = cell?.value ? cell.value.toString().trim() : '';
              });
              
              console.log('🧪 TEST: Headers:', actualHeaders);
              console.log('🧪 TEST: Complete row data:', completeRowData);
              
              if (authUser) {
                try {
                  // OLD SERVICE REMOVED - Using directExcelNotification instead
                  console.log('✅ TEST: Notification sent successfully!');
                  alert('✅ Test notification sent! Check user dashboard.');
                } catch (error) {
                  console.error('❌ TEST: Notification failed:', error);
                  alert('❌ Test notification failed. Check console.');
                }
              } else {
                alert('❌ No user logged in for test');
              }
            } else {
              alert('❌ No data in current sheet for test');
            }
          }} className="btn-debug" style={{backgroundColor: '#ff9800', color: 'white'}}>🧪 Test Data Flow</button> */}
          <button onClick={onClose} className="btn-close">❌ Close</button>
          <div className="save-status" style={{
            display: 'inline-flex',
            alignItems: 'center',
            marginLeft: '10px',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 'bold'
          }}>
            {autoSaveStatus === 'saving' && <span style={{color: '#ff9800'}}>🔄 Saving...</span>}
            {autoSaveStatus === 'saved' && <span style={{color: '#4caf50'}}>✅ Saved</span>}
            {autoSaveStatus === 'error' && <span style={{color: '#f44336'}}>❌ Error</span>}
            {lastSaved && autoSaveStatus === 'saved' && (
              <span style={{color: '#666', marginLeft: '5px', fontSize: '10px'}}>
                {lastSaved.toLocaleTimeString()}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Excel Toolbar - Hide in read-only mode */}
      {!isReadOnly && (
        <div className="excel-toolbar">
          <div className="toolbar-section">
            <button onClick={handleUndo} disabled={historyIndex <= 0}>↶ Undo</button>
            <button onClick={handleRedo} disabled={historyIndex >= history.length - 1}>↷ Redo</button>
            <div className="toolbar-divider"></div>
            <button onClick={handleCopy}>📋 Copy</button>
            <button onClick={handlePaste} disabled={!clipboard}>📋 Paste</button>
            <div className="toolbar-divider"></div>
            <button onClick={handleAddRowWithData}>➕ Add Row</button>
            <button onClick={handleAddColumn}>➕ Add Column</button>
            <button onClick={() => selectedCell && setEditingCell(selectedCell)} disabled={!selectedCell}>✏️ Edit Cell</button>
            <button onClick={handleDeleteRow} disabled={!selectedCell}>🗑️ Delete Row</button>
            <button onClick={handleDeleteColumn} disabled={!selectedCell}>🗑️ Delete Column</button>
            <div className="toolbar-divider"></div>
            <button onClick={() => setShowAddSheetModal(true)}>📄 Add Sheet</button>
            <button onClick={() => setShowUploadModal(true)}>📁 Upload File</button>
            <button onClick={() => setShowHeaderImportModal(true)}>📋 Import Headers</button>
            <button onClick={refreshData}>🔄 Refresh Data</button>
          <div className="toolbar-divider"></div>
          <button onClick={async () => {
            console.log('💾 IMMEDIATE Manual save triggered...');
            setAutoSaveStatus('saving');
            const result = await saveEntireWorkbook();
            if (result) {
              setAutoSaveStatus('saved');
              setLastSaved(new Date());
              alert('✅ IMMEDIATE Save successful!\nData saved to AWS S3.\n😊');
            } else {
              setAutoSaveStatus('error');
              alert('❌ IMMEDIATE Save failed! Check console for details.');
            }
          }} style={{background: '#28a745', color: 'white', fontWeight: 'bold', fontSize: '14px', padding: '8px 16px', animation: 'pulse 2s infinite'}}>💾 IMMEDIATE SAVE</button>
          {/* <button onClick={() => {
            console.log('🚨 EMERGENCY: Force populating data...');
            
            if (sheets.length === 0) {
              alert('❌ No sheets available');
              return;
            }
            
            const newSheets = [...sheets];
            const currentSheet = newSheets[activeSheetIndex];
            const targetRow = 1; // Use row 1
            
            if (currentSheet.data[targetRow]) {
              const headers = currentSheet.data[0] || [];
              console.log('🚨 EMERGENCY: Headers available:', headers.map(h => h?.value));
              
              // Use assignedTaskData if available, otherwise use test data
              const dataToUse = assignedTaskData || {
                'Particulars': 'Emergency Test Task',
                'Concerned Person': 'Emergency Test User',
                'Status': 'Emergency',
                'Priority': 'High',
                'Due Date': '2025-01-15'
              };
              
              console.log('🚨 EMERGENCY: Using data:', dataToUse);
              
              headers.forEach((headerCell, colIndex) => {
                const headerValue = String(headerCell?.value || '').trim();
                const taskValue = String(dataToUse[headerValue as keyof typeof dataToUse] || '');
                
                if (taskValue && currentSheet.data[targetRow][colIndex]) {
                  currentSheet.data[targetRow][colIndex] = {
                    ...currentSheet.data[targetRow][colIndex],
                    value: taskValue
                  };
                  console.log(`🚨 EMERGENCY: Populated [${targetRow}, ${colIndex}] (${headerValue}): "${taskValue}"`);
                }
              });
              
              setSheets(newSheets);
              console.log('✅ EMERGENCY: Data populated successfully');
              alert('✅ EMERGENCY: Data populated!\nCheck the yellow highlighted row now.');
            } else {
              alert('❌ Target row not available');
            }
          }} style={{background: '#ff5722', color: 'white', fontWeight: 'bold', fontSize: '14px', padding: '8px 16px'}}>🚨 EMERGENCY POPULATE</button> */}
          <button onClick={saveEntireSheet}>💾 Save Sheet</button>
          {/* <button onClick={async () => {
            console.log('🧪 Manual save test - current sheets:', sheets.map(s => s.name));
            await saveEntireWorkbook();
            console.log('🧪 Manual save completed');
          }} style={{background: '#ff6b6b', color: 'white', fontWeight: 'bold'}}>🧪 Test Save</button> */}
          {/* <button onClick={async () => {
            console.log('🧹 Clearing old metadata and forcing fresh save...');
            await excelAwsS3Service.clearOldMetadata(folderId);
            await saveEntireWorkbook();
            console.log('🧹 Fresh save completed');
          }} style={{background: '#9c27b0', color: 'white', fontWeight: 'bold'}}>🧹 Fresh Save</button> */}
          {/* <button onClick={forceSaveCurrentSheets} style={{background: '#ff9800', color: 'white', fontWeight: 'bold'}}>💾 Force Save</button> */}
         
          {/* <button onClick={() => {
            console.log('🔍 Storage Configuration Check:');
            console.log('AWS S3 Configured:', excelAwsS3Service.isConfigured());
            console.log('Environment Variables:');
            console.log('- REACT_APP_AWS_ACCESS_KEY_ID:', process.env.REACT_APP_AWS_ACCESS_KEY_ID ? '✅ Set' : '❌ Missing');
            console.log('- REACT_APP_AWS_SECRET_ACCESS_KEY:', process.env.REACT_APP_AWS_SECRET_ACCESS_KEY ? '✅ Set' : '❌ Missing');
            console.log('- REACT_APP_S3_BUCKET_NAME:', process.env.REACT_APP_S3_BUCKET_NAME ? '✅ Set' : '❌ Missing');
            console.log('- REACT_APP_AWS_REGION:', process.env.REACT_APP_AWS_REGION || 'us-east-1 (default)');
            alert(`Storage Status: ${excelAwsS3Service.isConfigured() ? '☁️ AWS S3' : '🔥 Firestore'}\nCheck console for details.`);
          }} style={{background: '#17a2b8', color: 'white', fontWeight: 'bold'}}>🔍 Check Storage</button> */}
          {/* <button onClick={async () => {
            console.log('🧪 TEST: Manual save with detailed logging...');
            console.log('📊 Current sheets before save:', sheets.map(s => ({
              name: s.name,
              dataLength: s.data.length,
              hasContent: s.data.some(row => row.some(cell => cell?.value && String(cell.value).trim() !== ''))
            })));
            const result = await saveEntireWorkbook();
            console.log('🧪 TEST: Save result:', result);
            if (result) {
              console.log('✅ Manual save successful!');
            } else {
              console.log('❌ Manual save failed!');
            }
          }} style={{background: '#e91e63', color: 'white', fontWeight: 'bold'}}>🧪 Test Save</button> */}
          {/* <button onClick={async () => {
            console.log('🔄 TEST: Manual refresh to check data loading...');
            console.log('📊 Current sheets before refresh:', sheets.map(s => ({
              name: s.name,
              dataLength: s.data.length,
              hasContent: s.data.some(row => row.some(cell => cell?.value && String(cell.value).trim() !== ''))
            })));
            await loadFolderData();
            console.log('📊 Sheets after refresh:', sheets.map(s => ({
              name: s.name,
              dataLength: s.data.length,
              hasContent: s.data.some(row => row.some(cell => cell?.value && String(cell.value).trim() !== ''))
            })));
          }} style={{background: '#ff5722', color: 'white', fontWeight: 'bold'}}>🔄 Test Refresh</button> */}
          {/* <button onClick={async () => {
            console.log('🧪 TEST: Add test data and save...');
            if (sheets.length > 0) {
              const newSheets = [...sheets];
              const currentSheet = newSheets[activeSheetIndex];
              if (currentSheet && currentSheet.data.length > 0) {
                // Add a test row
                const testRow = currentSheet.data[0].map((_, index) => ({
                  value: index === 0 ? `TEST_${Date.now()}` : `Test Data ${index}`,
                  format: {}
                }));
                currentSheet.data.push(testRow);
                newSheets[activeSheetIndex] = currentSheet;
                setSheets(newSheets);
                console.log('✅ Test data added to sheet');
                
                // Save immediately
                setTimeout(async () => {
                  const result = await saveEntireWorkbook();
                  console.log('🧪 TEST: Save result after adding test data:', result);
                }, 1000);
              }
            }
          }} style={{background: '#4caf50', color: 'white', fontWeight: 'bold'}}>➕ Add Test Data</button> */}

          <div className="toolbar-divider"></div>
          {/* <button onClick={testNotification} style={{background: '#ff6b6b', color: 'white', fontWeight: 'bold'}}>🔔 Test Notification (Firestore)</button> */}
          {/* <button onClick={() => {
            console.log('🔍 DEBUG: Current state check...');
            console.log('🔍 Sheets:', sheets?.length, sheets);
            console.log('🔍 Active sheet index:', activeSheetIndex);
            console.log('🔍 Is consolidated view:', isConsolidatedView);
            console.log('🔍 All task rows:', allTaskRows?.length, allTaskRows);
            console.log('🔍 Headers:', headers);
            console.log('🔍 Folder ID:', folderId);
            console.log('🔍 Assigned task data:', assignedTaskData);
            
            if (sheets && sheets[0]) {
              const currentSheet = sheets[0];
              console.log('🔍 Current sheet name:', currentSheet.name);
              console.log('🔍 Current sheet data rows:', currentSheet.data.length);
              console.log('🔍 First 3 rows:', currentSheet.data.slice(0, 3));
            }
          }} style={{background: '#9c27b0', color: 'white', fontWeight: 'bold'}}>🔍 Debug State</button> */}
          {/* <button onClick={async () => {
            try {
              console.log('🧪 Testing Excel assignment notification (AWS S3)...');
              if (!authUser) {
                alert('No user logged in');
                return;
              }
              
              // OLD SERVICE REMOVED - Using directExcelNotification instead
              console.log('✅ Test Excel assignment notification sent via AWS S3!');
              alert('✅ Test Excel assignment notification sent via AWS S3! Check console for details.');
            } catch (error) {
              console.error('❌ Test Excel assignment notification failed:', error);
              alert('❌ Test Excel assignment notification failed. Check console for details.');
            }
          }} style={{background: '#9c27b0', color: 'white', fontWeight: 'bold'}}>🧪 Test AWS S3 Assignment</button>
         
          <button onClick={async () => {
            try {
              console.log('🧪 Testing AWS S3 notification system...');
              if (!authUser) {
                alert('No user logged in');
                return;
              }
              
              // OLD SERVICE REMOVED - Using directExcelNotification instead
              console.log('✅ AWS S3 notification system test completed!');
              alert('✅ AWS S3 notification system test completed! Check console for details.');
            } catch (error) {
              console.error('❌ AWS S3 notification system test failed:', error);
              alert('❌ AWS S3 notification system test failed. Check console for details.');
            }
          }} style={{background: '#2196f3', color: 'white', fontWeight: 'bold'}}>🧪 Test AWS S3 System</button> */}
          {/* <button onClick={async () => {
            try {
              console.log('🧪 Testing direct local notification...');
              if (!authUser) {
                alert('No user logged in');
                return;
              }
              
              // Test direct local storage
              const localNotifications = JSON.parse(localStorage.getItem('excelNotifications') || '[]');
              const testNotification = {
                userId: authUser.uid,
                type: 'EXCEL_ASSIGNMENT',
                title: 'Direct Local Test',
                message: 'This is a direct local storage test',
                data: { test: true },
                read: false,
                createdAt: new Date().toISOString(),
                id: `local_test_${Date.now()}`,
                isLocal: true
              };
              localNotifications.push(testNotification);
              localStorage.setItem('excelNotifications', JSON.stringify(localNotifications));
              console.log('✅ Direct local notification stored!');
              alert('✅ Direct local notification stored! Check localStorage.');
            } catch (error) {
              console.error('❌ Direct local notification failed:', error);
              alert('❌ Direct local notification failed. Check console for details.');
            }
          }} style={{background: '#ff5722', color: 'white', fontWeight: 'bold'}}>🧪 Test Local Only</button> */}
          {/* <button onClick={testMultipleUserSelection} style={{background: '#10b981', color: 'white', fontWeight: 'bold'}}>👥 Test Multi-User</button> */}
          {/* <button onClick={migrateExcelDataToFixedHeaders} style={{background: '#ff9800', color: 'white', fontWeight: 'bold'}}>🔄 Migrate to Fixed Headers</button> */}
          {/* <button onClick={async () => {
            try {
              console.log('🧪 Testing direct notification system...');
              
              // Test with current user
              const testUserId = currentUser?.uid || 'test-user';
              const testExcelData = {
                headers: ['Particulars', 'Concerned Person', 'Status', 'Priority', 'Due Date'],
                rows: [['Test Task', 'Test User', 'In Progress', 'High', '2024-01-31']],
                sheetName: 'Test Sheet',
                workbookId: 'test-workbook'
              };
              
              const result = await directExcelNotification.sendDirectExcelNotification(
                testUserId,
                testExcelData,
                currentUser?.name || 'Test Admin',
                0
              );
              
              if (result.success) {
                console.log('✅ Direct notification test successful!');
                alert('✅ Direct notification system working!\nTest notification sent successfully.');
              } else {
                console.error('❌ Direct notification test failed:', result.error);
                alert('❌ Direct notification test failed: ' + result.error);
              }
            } catch (error) {
              console.error('❌ Direct notification test error:', error);
              alert('❌ Direct notification test error: ' + (error instanceof Error ? error.message : String(error)));
            }
          }} style={{background: '#00bcd4', color: 'white', fontWeight: 'bold'}}>🧪 Test Direct Notification</button> */}
          {/* <button onClick={() => {
            console.log('🔍 SYSTEM STATUS CHECK:');
            console.log('📊 Current user:', currentUser);
            console.log('📊 Sheets count:', sheets.length);
            console.log('📊 Active sheet:', activeSheet?.name);
            console.log('📊 Headers:', activeSheet?.data?.[0]?.map(cell => cell?.value));
            console.log('📊 CORE_HEADERS:', CORE_HEADERS);
            console.log('📊 Direct notification service:', directExcelNotification);
            
            const statusMessage = `
SYSTEM STATUS:
✅ User: ${currentUser?.name || 'Not logged in'}
✅ Sheets: ${sheets.length}
✅ Active Sheet: ${activeSheet?.name || 'None'}
✅ Headers: ${activeSheet?.data?.[0]?.map(cell => cell?.value).join(', ') || 'None'}
✅ CORE_HEADERS: ${CORE_HEADERS.join(', ')}
✅ Direct Notification Service: ${directExcelNotification ? 'Available' : 'Not Available'}

Everything looks good! System is ready to use.
            `;
            
            alert(statusMessage);
            console.log('✅ System status check complete');
          }} style={{background: '#4caf50', color: 'white', fontWeight: 'bold'}}>🔍 System Status</button>

          <button onClick={() => {
            try {
              console.log('📝 Adding sample data for testing...');
              
              if (sheets.length === 0 || !activeSheet) {
                alert('No active sheet found. Please create a sheet first.');
                return;
              }
              
              const newSheets = [...sheets];
              const currentSheet = newSheets[activeSheetIndex];
              
              // Find first empty row
              let emptyRowIndex = 1;
              while (emptyRowIndex < currentSheet.data.length && 
                     currentSheet.data[emptyRowIndex].some(cell => cell?.value)) {
                emptyRowIndex++;
              }
              
              if (emptyRowIndex >= currentSheet.data.length) {
                // Add new row
                const newRow = CORE_HEADERS.map(() => ({ value: '' }));
                currentSheet.data.push(newRow);
              }
              
              // Add sample data
              const sampleData = [
                'Sample Task - ' + Date.now(),
                'Test User',
                'In Progress',
                'Medium',
                '2024-02-15'
              ];
              
              sampleData.forEach((value, colIndex) => {
                if (currentSheet.data[emptyRowIndex]) {
                  currentSheet.data[emptyRowIndex][colIndex] = { value };
                }
              });
              
              setSheets(newSheets);
              saveToHistory(newSheets);
              
              console.log('✅ Sample data added successfully');
              alert('✅ Sample data added to row ' + (emptyRowIndex + 1) + '\n\nYou can now test the notification system!');
              
            } catch (error) {
              console.error('❌ Error adding sample data:', error);
              alert('❌ Error adding sample data: ' + (error instanceof Error ? error.message : String(error)));
            }
          }} style={{background: '#9c27b0', color: 'white', fontWeight: 'bold'}}>📝 Add Sample Data</button> */}

          <button onClick={async () => {
            try {
              console.log('🔄 Force migrating existing sheet headers...');
              const migratedSheets = migrateSheetHeaders(sheets, true); // Force migration
              setSheets(migratedSheets);
              saveToHistory(migratedSheets);
              console.log('✅ Sheet headers force migrated successfully!');
              alert('✅ Sheet headers force migrated successfully! Headers updated to new structure.');
            } catch (error) {
              console.error('❌ Header migration failed:', error);
              alert('❌ Header migration failed. Check console for details.');
            }
          }} style={{background: '#ff9800', color: 'white', fontWeight: 'bold'}}>🔄 Force Migrate Headers</button>
          {/* <button onClick={() => {
            console.log('🔍 Debug: Current sheet data structure:');
            console.log('📊 Active sheet:', activeSheet?.name);
            console.log('📋 Headers:', activeSheet?.data[0]?.map(cell => cell?.value));
            console.log('📊 Sample data row:', activeSheet?.data[1]?.map(cell => cell?.value));
            console.log('📊 All data rows:', activeSheet?.data.slice(1).map((row, index) => ({
              rowIndex: index + 1,
              data: row.map(cell => cell?.value)
            })));
            alert('🔍 Debug data logged to console. Check browser console for details.');
          }} style={{background: '#9c27b0', color: 'white', fontWeight: 'bold'}}>🔍 Debug Data</button> */}

          <div className="toolbar-divider"></div>
          <button onClick={() => handleFormatCell('bold')} disabled={!selectedCell}>B</button>
          <button onClick={() => handleFormatCell('italic')} disabled={!selectedCell}>I</button>
          <button onClick={() => handleFormatCell('center')} disabled={!selectedCell}>Center</button>
          <button onClick={() => handleFormatCell('left')} disabled={!selectedCell}>Left</button>
          <button onClick={() => handleFormatCell('right')} disabled={!selectedCell}>Right</button>
        </div>
        
      </div>
      )}

      {/* Sheet Tabs - Hide in consolidated view */}
      {!isConsolidatedView && (
        <div className="sheet-tabs-container">
          <span className="sheet-tabs-label">Sheets:</span>
            <div className="sheet-tabs">
            {sheets.length === 0 ? (
              <div className="sheet-tab-container">
                <button className="sheet-tab active">Default Sheet</button>
              </div>
            ) : (
              sheets.map((sheet, index) => (
                <div key={index} className="sheet-tab-container">
                  <button
                    className={`sheet-tab ${index === activeSheetIndex ? 'active' : ''}`}
                    onClick={() => setActiveSheetIndex(index)}
                  >
                    {sheet.name}
                  </button>
                  <div className="sheet-tab-actions">
                    <button
                      className="sheet-action-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        const newName = prompt('Rename sheet:', sheet.name);
                        if (newName && newName !== sheet.name) {
                          handleRenameSheet(index, newName);
                        }
                      }}
                      title="Rename sheet"
                    >
                      ✏️
                    </button>
                    <button
                      className="sheet-action-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm(`Delete sheet "${sheet.name}"?`)) {
                          handleDeleteSheet(index);
                        }
                      }}
                      title="Delete sheet"
                      disabled={sheets.length <= 1}
                    >
                      🗑️
                    </button>
                  </div>
                </div>
            )))}
            </div>
          <button onClick={() => setShowAddSheetModal(true)} className="add-sheet-btn">
            ➕ Add Sheet
          </button>
        </div>
      )}

      {/* Consolidated View Header */}
      {isConsolidatedView && (
        <div className="consolidated-view-header">
          <div className="consolidated-header-content">
            <h2>📊 My Assigned Tasks - Single Consolidated Sheet</h2>
            <p>All your assigned tasks in one unified Excel sheet</p>
          </div>
        </div>
      )}

      {/* Formula Bar */}
      <div className="formula-bar">
        <span className="cell-address">
          {selectedCell ? `${COLUMN_HEADERS[selectedCell.col]}${selectedCell.row}` : 'A1'}
        </span>
        {isReadOnly ? (
          <div
            style={{
              width: '100%',
              padding: '8px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontSize: '14px',
              backgroundColor: '#f3f4f6',
              color: '#6b7280',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            🔒 <span style={{ fontWeight: 'bold' }}>READ-ONLY MODE</span> - {formulaBar || 'Select a cell to view its value'}
          </div>
        ) : (
          <input
            ref={formulaBarRef}
            type="text"
            value={formulaBar}
            onChange={(e) => handleFormulaBarChange(e.target.value)}
            onKeyDown={handleFormulaBarKeyDown}
            placeholder="Enter formula or value"
            className="formula-input"
            style={{
              width: '100%',
              padding: '8px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontSize: '14px',
              outline: 'none',
              backgroundColor: 'white'
            }}
          />
        )}
      </div>

      {/* Excel Grid */}
      <div 
        className="excel-grid-container" 
        ref={containerRef}
        onKeyDown={handleKeyDown}
        tabIndex={0}
      >
        <div className="excel-grid" key={`grid-${forceRender}`}>
          {/* Column Headers */}
          <div className="excel-header-row">
            {Array.from({ length: Math.max(sheets[activeSheetIndex]?.data[0]?.length || 0, 20) }, (_, index) => {
              // Ensure the header cell exists, create it if it doesn't
              if (!sheets[activeSheetIndex]?.data[0]) {
                const newSheets = [...sheets];
                newSheets[activeSheetIndex] = {
                  ...newSheets[activeSheetIndex],
                  data: [...newSheets[activeSheetIndex].data]
                };
                newSheets[activeSheetIndex].data[0] = [];
                setSheets(newSheets);
              }
              if (!sheets[activeSheetIndex].data[0][index]) {
                const newSheets = [...sheets];
                newSheets[activeSheetIndex] = {
                  ...newSheets[activeSheetIndex],
                  data: [...newSheets[activeSheetIndex].data]
                };
                if (!newSheets[activeSheetIndex].data[0]) {
                  newSheets[activeSheetIndex].data[0] = [];
                }
                newSheets[activeSheetIndex].data[0][index] = { 
                  value: `Column ${index + 1}`, 
                  format: { bold: true, backgroundColor: '#f0f0f0', textAlign: 'center' as const } 
                };
                setSheets(newSheets);
              }
              const cell = sheets[activeSheetIndex].data[0][index];
              return (
              <div 
                key={index} 
                className={`excel-cell header-cell column-header ${
                  String(cell.value).toLowerCase() === 'concerned person' || 
                  String(cell.value).toLowerCase().includes('assigned to') ? 'whatsapp-notification-column' : ''
                }`}
                style={{ 
                  width: columnWidths[index] || 200, // Increased to 200 for better header display
                  height: rowHeights[0] || 32,
                  ...cell.format
                }}
                onDoubleClick={() => {
                  if (!isReadOnly) {
                    handleHeaderDoubleClick(index);
                  }
                }}
                onClick={(e) => {
                  e.preventDefault();
                  setSelectedCell({ row: 0, col: index });
                  // Block header editing in read-only mode
                  if (!isReadOnly) {
                    setEditingCell({ row: 0, col: index });
                  }
                  setCellValue(String(cell.value || ''));
                  setFormulaBar(cell.formula || String(cell.value || ''));
                  
                  // Removed formula bar focus to prevent infinite loops
                }}
              >
                {editingCell?.row === 0 && editingCell?.col === index && !isReadOnly ? (
                  <input
                    type="text"
                    value={cellValue}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      console.log(`📝 Header input changed to: "${newValue}" for [0, ${index}]`);
                      setCellValue(newValue);
                      setFormulaBar(newValue);
                      // Update header cell in real-time as user types
                      updateCell(0, index, newValue);
                    }}
                    onBlur={async () => {
                      await saveCellValue(0, index);
                      setEditingCell(null);
                    }}
                    onKeyDown={async (e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        await saveCellValue(0, index);
                        setEditingCell(null);
                      } else if (e.key === 'Escape') {
                        e.preventDefault();
                        setEditingCell(null);
                        setCellValue(String(cell.value || ''));
                        setFormulaBar(cell.formula || String(cell.value || ''));
                      } else if (e.key === 'Tab') {
                        e.preventDefault();
                        await saveCellValue(0, index);
                        setEditingCell(null);
                        // Move to next header cell
                        if (index < sheets[activeSheetIndex].data[0].length - 1) {
                          setSelectedCell({ row: 0, col: index + 1 });
                          setEditingCell({ row: 0, col: index + 1 });
                        }
                      }
                    }}
                    // Removed autoFocus to prevent infinite loops
                    className="cell-input"
                    tabIndex={-1}
                    // Removed onFocus to prevent infinite loops
                    onMouseDown={(e) => {
                      // Prevent focus on mouse down
                      e.preventDefault();
                    }}
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none',
                      outline: 'none',
                      background: 'white',
                      fontSize: 'inherit',
                      fontFamily: 'inherit',
                      padding: '2px',
                      margin: '0',
                      textAlign: 'center',
                      fontWeight: 'bold',
                      boxShadow: '0 0 0 2px #3b82f6'
                    }}
                  />
                ) : (
                  <span className="cell-display" title={`Cell [0, ${index}] value: "${cell.value}"`}>
                    {cell.value}
                  </span>
                )}
                <div 
                  className="resize-handle column-resize"
                  onMouseDown={(e) => handleResizeStart('column', index, e)}
                ></div>
              </div>
              );
            })}
          </div>

          {/* Data Rows */}
          {activeSheet.data.slice(1).map((row, rowIndex) => {
            const isHighlightedRow = highlightRow === (rowIndex + 1); // +1 because we're skipping row 0 and using 1-based indexing
            return (
            <div 
              key={rowIndex} 
              className={`excel-row ${isHighlightedRow ? 'highlighted-assigned-row' : ''}`}
              style={{ 
                height: rowHeights[rowIndex] || 32,
                backgroundColor: isHighlightedRow ? '#fef3c7' : undefined,
                border: isHighlightedRow ? '2px solid #f59e0b' : undefined
              }}
              data-row={rowIndex + 1}
            >
              {Array.from({ length: Math.max(row.length, 20) }, (_, colIndex) => {
                // Ensure the cell exists, create it if it doesn't
                if (!row[colIndex]) {
                  row[colIndex] = { value: '', format: {} };
                }
                const cell = row[colIndex];
                return (
                  <div
                    key={colIndex}
                    className={`excel-cell data-cell ${
                      selectedCell?.row === (rowIndex + 1) && selectedCell?.col === colIndex ? 'selected' : ''
                    } ${editingCell?.row === (rowIndex + 1) && editingCell?.col === colIndex ? 'editing' : ''} ${
                      (String(sheets[activeSheetIndex].data[0][colIndex]?.value || '').toLowerCase() === 'concerned person' || 
                      String(sheets[activeSheetIndex].data[0][colIndex]?.value || '').toLowerCase().includes('assigned to')) ? 'whatsapp-notification-cell' : ''
                    }`}
                    data-header={String(sheets[activeSheetIndex].data[0][colIndex]?.value || '')}
                    onClick={(e) => {
                      // Don't handle clicks when already editing this cell
                      const isCurrentlyEditing = editingCell?.row === (rowIndex + 1) && editingCell?.col === colIndex;
                      if (!isCurrentlyEditing) {
                        e.preventDefault();
                        handleCellClickMemo(rowIndex + 1, colIndex, e);
                      }
                    }}
                    onDoubleClick={(e) => {
                      e.preventDefault();
                      handleCellDoubleClickMemo(rowIndex + 1, colIndex);
                    }}
                    onKeyDown={(e) => {
                      // Don't handle keydown when already editing this cell
                      const isCurrentlyEditing = editingCell?.row === (rowIndex + 1) && editingCell?.col === colIndex;
                      if (!isCurrentlyEditing) {
                        handleCellKeyDownMemo(e, rowIndex + 1, colIndex);
                      }
                    }}
                    onContextMenu={(e) => handleContextMenuMemo(e, rowIndex + 1, colIndex)}
                    // Removed tabIndex and onFocus to prevent infinite loops - selection handled by onClick
                    style={{
                      width: columnWidths[colIndex] || 200,
                      height: rowHeights[rowIndex + 1] || 32,
                      fontWeight: cell.format?.bold ? 'bold' : 'normal',
                      fontStyle: cell.format?.italic ? 'italic' : 'normal',
                      color: cell.format?.color || 'inherit',
                      backgroundColor: cell.format?.backgroundColor || 'inherit',
                      textAlign: cell.format?.textAlign || 'left'
                    }}
                  >
            {editingCell?.row === (rowIndex + 1) && editingCell?.col === colIndex && !isReadOnly ? (
              (() => {
                const headerValue = String(sheets[activeSheetIndex].data[0][colIndex]?.value || '').toLowerCase();
                const hasAssignedTo = headerValue.includes('assigned to');
                const hasConcernedPerson = headerValue === 'concerned person'; // Exact match only
                const shouldShowUserSelector = hasAssignedTo || hasConcernedPerson;
                
                // Check if this is the Attachment column
                const isAttachmentColumn = headerValue === 'attachment';
                
                // Special handling for Attachment column
                if (isAttachmentColumn) {
                  const cellValue = String(cell.value || '');
                  const hasAttachment = cellValue && cellValue !== '';
                  
                  // Parse filename and URL if exists (format: filename|url)
                  let filename = '';
                  let fileUrl = '';
                  if (hasAttachment && typeof cellValue === 'string' && cellValue.includes('|')) {
                    [filename, fileUrl] = cellValue.split('|');
                  } else if (hasAttachment) {
                    fileUrl = cellValue;
                    filename = 'View Document';
                  }
                  
                  return (
                    <div style={{ padding: '4px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      {hasAttachment ? (
                        <>
                          <a 
                            href={fileUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{
                              color: '#2563eb',
                              textDecoration: 'underline',
                              cursor: 'pointer',
                              fontSize: '13px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            📎 {filename}
                          </a>
                          {!isReadOnly && (
                            <button
                              onClick={() => {
                                if (window.confirm(`Remove attachment "${filename}"?`)) {
                                  updateCell(rowIndex + 1, colIndex, '');
                                  saveEntireWorkbook();
                                }
                              }}
                              style={{
                                fontSize: '11px',
                                padding: '2px 6px',
                                color: '#dc2626',
                                background: '#fee2e2',
                                border: '1px solid #dc2626',
                                borderRadius: '3px',
                                cursor: 'pointer'
                              }}
                            >
                              🗑️ Remove
                            </button>
                          )}
                        </>
                      ) : (
                        !isReadOnly && (
                          <input
                            type="file"
                            onChange={(e) => handleAttachmentUpload(e, rowIndex + 1, colIndex)}
                            accept="*/*"
                            style={{
                              fontSize: '12px',
                              padding: '4px',
                              cursor: 'pointer'
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                        )
                      )}
                    </div>
                  );
                }
                
                // Check for status dropdown
                if (headerValue.includes('status')) {
                  return (
                    <select
                      value={cellValue}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setCellValue(newValue);
                        setFormulaBar(newValue);
                        // Don't update cell immediately - wait for onBlur or onKeyDown
                      }}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      onBlur={async () => {
                        await saveCellValue(rowIndex + 1, colIndex);
                        setEditingCell(null);
                      }}
                      onKeyDown={async (e) => {
                        if (e.key === 'Enter' || e.key === 'Tab') {
                          e.preventDefault();
                          await saveCellValue(rowIndex + 1, colIndex);
                          setEditingCell(null);
                          if (e.key === 'Enter') moveToNextRow(rowIndex + 1, colIndex);
                          else moveToNextCol(rowIndex + 1, colIndex);
                        } else if (e.key === 'Escape') {
                          e.preventDefault();
                          setEditingCell(null);
                          setCellValue(String(cell.value || ''));
                          setFormulaBar(cell.formula || String(cell.value || ''));
                        }
                      }}
                      // Removed autoFocus to prevent infinite loops
                      className="cell-input cell-dropdown"
                      tabIndex={-1}
                      style={{
                        width: '100%',
                        height: '100%',
                        border: 'none',
                        outline: 'none',
                        background: 'white',
                        fontSize: 'inherit',
                        fontFamily: 'inherit',
                        padding: '2px',
                        margin: '0',
                        boxShadow: '0 0 0 2px #3b82f6',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="">Select Status</option>
                      <option value="TO-DO">TO-DO</option>
                      <option value="IN-PROGRESS">IN-PROGRESS</option>
                      <option value="COMPLETED">COMPLETED</option>
                    </select>
                  );
                }
                
                // Check for priority dropdown
                if (headerValue.includes('priority')) {
                  return (
                    <select
                      value={cellValue}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setCellValue(newValue);
                        setFormulaBar(newValue);
                        // Don't update cell immediately - wait for onBlur or onKeyDown
                      }}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      onBlur={async () => {
                        await saveCellValue(rowIndex + 1, colIndex);
                        setEditingCell(null);
                      }}
                      onKeyDown={async (e) => {
                        if (e.key === 'Enter' || e.key === 'Tab') {
                          e.preventDefault();
                          await saveCellValue(rowIndex + 1, colIndex);
                          setEditingCell(null);
                          if (e.key === 'Enter') moveToNextRow(rowIndex + 1, colIndex);
                          else moveToNextCol(rowIndex + 1, colIndex);
                        } else if (e.key === 'Escape') {
                          e.preventDefault();
                          setEditingCell(null);
                          setCellValue(String(cell.value || ''));
                          setFormulaBar(cell.formula || String(cell.value || ''));
                        }
                      }}
                      // Removed autoFocus to prevent infinite loops
                      className="cell-input cell-dropdown"
                      tabIndex={-1}
                      style={{
                        width: '100%',
                        height: '100%',
                        border: 'none',
                        outline: 'none',
                        background: 'white',
                        fontSize: 'inherit',
                        fontFamily: 'inherit',
                        padding: '2px',
                        margin: '0',
                        boxShadow: '0 0 0 2px #3b82f6',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="">Select Priority</option>
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  );
                }
                
                // Check for date fields
                if (headerValue.includes('date') || headerValue.includes('created') || headerValue.includes('due')) {
                  return (
                    <input
                      type="date"
                      value={cellValue}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setCellValue(newValue);
                        setFormulaBar(newValue);
                        // Don't update cell immediately - wait for onBlur or onKeyDown
                      }}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      onBlur={async () => {
                        await saveCellValue(rowIndex + 1, colIndex);
                        setEditingCell(null);
                      }}
                      onKeyDown={async (e) => {
                        if (e.key === 'Enter' || e.key === 'Tab') {
                          e.preventDefault();
                          await saveCellValue(rowIndex + 1, colIndex);
                          setEditingCell(null);
                          if (e.key === 'Enter') moveToNextRow(rowIndex + 1, colIndex);
                          else moveToNextCol(rowIndex + 1, colIndex);
                        } else if (e.key === 'Escape') {
                          e.preventDefault();
                          setEditingCell(null);
                          setCellValue(String(cell.value || ''));
                          setFormulaBar(cell.formula || String(cell.value || ''));
                        }
                      }}
                      // Removed autoFocus to prevent infinite loops
                      className="cell-input cell-date"
                      tabIndex={-1}
                      style={{
                        width: '100%',
                        height: '100%',
                        border: 'none',
                        outline: 'none',
                        background: 'white',
                        fontSize: 'inherit',
                        fontFamily: 'inherit',
                        padding: '2px',
                        margin: '0',
                        boxShadow: '0 0 0 2px #3b82f6'
                      }}
                    />
                  );
                }
                
                // Check for ID field - auto-generate
                if (headerValue.includes('id') && rowIndex > 0) {
                  const autoGeneratedId = `TASK_${Date.now()}_${rowIndex}`;
                  return (
                    <input
                      type="text"
                      value={cellValue || autoGeneratedId}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setCellValue(newValue);
                        setFormulaBar(newValue);
                        // Don't update cell immediately - wait for onBlur or onKeyDown
                      }}
                      onBlur={async () => {
                        await saveCellValue(rowIndex + 1, colIndex);
                        setEditingCell(null);
                      }}
                      onKeyDown={async (e) => {
                        if (e.key === 'Enter' || e.key === 'Tab') {
                          e.preventDefault();
                          await saveCellValue(rowIndex + 1, colIndex);
                          setEditingCell(null);
                          if (e.key === 'Enter') moveToNextRow(rowIndex + 1, colIndex);
                          else moveToNextCol(rowIndex + 1, colIndex);
                        } else if (e.key === 'Escape') {
                          e.preventDefault();
                          setEditingCell(null);
                          setCellValue(String(cell.value || ''));
                          setFormulaBar(cell.formula || String(cell.value || ''));
                        }
                      }}
                      autoFocus
                      className="cell-input cell-id"
                      style={{
                        width: '100%',
                        height: '100%',
                        border: 'none',
                        outline: 'none',
                        background: '#f0f9ff',
                        fontSize: 'inherit',
                        fontFamily: 'inherit',
                        padding: '2px',
                        margin: '0',
                        boxShadow: '0 0 0 2px #3b82f6',
                        color: '#1e40af',
                        fontWeight: 'bold'
                      }}
                    />
                  );
                }
                
                // Check for user assignment column
                if (shouldShowUserSelector) {
                  return (
                <UserSelector
                  key={`user-selector-${rowIndex}-${colIndex}`}
                  value={cellValue}
                  onChange={(newValue) => {
                    console.log(`🔔 UserSelector onChange: "${newValue}" for cell [${rowIndex + 1}, ${colIndex}]`);
                    setCellValue(newValue);
                    setFormulaBar(newValue);
                    // Don't update cell immediately - wait for onBlur
                  }}
                  onBlur={async () => {
                    console.log(`🔔 UserSelector onBlur: "${cellValue}" for cell [${rowIndex + 1}, ${colIndex}]`);
                    // Use the current cell value from the sheet data, not the local cellValue state
                    const currentCellData = sheets[activeSheetIndex]?.data?.[rowIndex + 1]?.[colIndex];
                    const currentValue = currentCellData?.value || cellValue;
                    console.log(`🔔 UserSelector onBlur: Using current value "${currentValue}" for cell [${rowIndex + 1}, ${colIndex}]`);
                    await saveCellValue(rowIndex + 1, colIndex);
                    setEditingCell(null);
                  }}
                  placeholder="Select user..."
                  className="cell-input"
                  multiple={true}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    outline: 'none',
                    background: 'white',
                    fontSize: 'inherit',
                    fontFamily: 'inherit',
                    padding: '2px',
                    margin: '0',
                    boxShadow: '0 0 0 2px #3b82f6'
                  }}
                />
                  );
                }
                
                // Default text input
                return (
                <input
                  type="text"
                  value={cellValue}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setCellValue(newValue);
                    setFormulaBar(newValue);
                    // Don't update cell immediately - wait for onBlur or onKeyDown
                  }}
                  onBlur={async () => {
                    await saveCellValue(rowIndex + 1, colIndex);
                    setEditingCell(null);
                  }}
                  onKeyDown={async (e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      await saveCellValue(rowIndex + 1, colIndex);
                      setEditingCell(null);
                      moveToNextRow(rowIndex + 1, colIndex);
                    } else if (e.key === 'Tab') {
                      e.preventDefault();
                      await saveCellValue(rowIndex + 1, colIndex);
                      setEditingCell(null);
                      moveToNextCol(rowIndex + 1, colIndex);
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      setEditingCell(null);
                      setCellValue(String(cell.value || ''));
                      setFormulaBar(cell.formula || String(cell.value || ''));
                    }
                  }}
                  autoFocus
                  className="cell-input"
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    outline: 'none',
                    background: 'white',
                    fontSize: 'inherit',
                    fontFamily: 'inherit',
                    padding: '2px',
                    margin: '0',
                    boxShadow: '0 0 0 2px #3b82f6'
                  }}
                />
                );
              })()
            ) : (
              (() => {
                const headerValue = String(sheets[activeSheetIndex].data[0][colIndex]?.value || '').toLowerCase();
                const isAttachmentColumn = headerValue === 'attachment';
                
                // Special display for Attachment column
                if (isAttachmentColumn) {
                  const cellValue = String(cell.value || '');
                  const hasAttachment = cellValue && cellValue !== '';
                  
                  if (hasAttachment) {
                    // Parse filename and URL if exists (format: filename|url)
                    let filename = '';
                    let fileUrl = '';
                    if (cellValue.includes('|')) {
                      [filename, fileUrl] = cellValue.split('|');
                    } else {
                      fileUrl = String(cellValue);
                      filename = 'View Document';
                    }
                    
                    return (
                      <a 
                        href={fileUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{
                          color: '#2563eb',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          fontSize: '13px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '4px'
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        📎 {filename}
                      </a>
                    );
                  } else {
                    return (
                      <span style={{ color: '#9ca3af', fontSize: '12px', padding: '4px' }}>
                        {isReadOnly ? '(No attachment)' : '(Click to upload)'}
                      </span>
                    );
                  }
                }
                
                // Default display for other columns
                return (
                  <span className="cell-display">
                    {formatCellValue(cell, String(sheets[activeSheetIndex].data[0][colIndex]?.value || ''))}
                  </span>
                );
              })()
            )}
          </div>
                );
              })}
            </div>
          );
        })}
        </div>
      </div>

      {/* Status Bar */}
      <div className="status-bar">
        <div className="status-left">
          <span>Ready</span>
          {selectedCell && (
            <span> | Cell: {COLUMN_HEADERS[selectedCell.col]}{selectedCell.row + 1}</span>
          )}
        </div>
        <div className="status-right">
          <span className={`storage-status ${excelAwsS3Service.isConfigured() ? 'aws-s3' : 'firestore'}`}>
            {excelAwsS3Service.isConfigured() ? '☁️ AWS S3' : '🔥 Firestore'}
            {!navigator.onLine && ' (Offline)'}
          </span>
          <span className={`auto-save-status ${autoSaveStatus}`} style={{ 
            opacity: autoSaveStatus === 'saving' ? 0.7 : 1,
            transition: 'opacity 0.3s ease'
          }}>
            {autoSaveStatus === 'saving' && '💾 Saving...'}
            {autoSaveStatus === 'saved' && lastSaved && `💾 Saved ${lastSaved.toLocaleTimeString()}`}
            {autoSaveStatus === 'error' && '⚠️ Save Failed - Click SAVE TO BROWSER'}
          </span>
          <button 
            onClick={async () => {
              console.log('💾 Manual save from header...');
              const result = await saveEntireWorkbook();
              if (result) {
                alert('✅ Workbook saved successfully!\n\nNote: Data was saved to browser storage (localStorage) because AWS S3 is not configured yet.\nTo enable cloud storage, configure AWS S3 credentials.');
              } else {
                alert('❌ Failed to save workbook.\n\nPlease check your internet connection and try again.\nIf the problem persists, contact your administrator.');
              }
            }}
            className={`manual-save-btn ${autoSaveStatus === 'error' ? 'error' : ''}`}
            style={{
              background: autoSaveStatus === 'error' ? '#dc3545' : '#007bff',
              color: 'white',
              border: 'none',
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '12px',
              cursor: 'pointer',
              marginLeft: '8px'
            }}
          >
            {autoSaveStatus === 'saving' ? '⏳ Saving...' : autoSaveStatus === 'error' ? '💾 SAVE TO BROWSER' : '💾 SAVE NOW'}
          </button>
          <button 
            onClick={async () => {
              try {
                console.log('🧪 Testing Excel notification...');
                // OLD SERVICE REMOVED - Using directExcelNotification instead
                console.log('✅ Test notification sent successfully!');
                alert('✅ Test notification sent! Check the notification bell.');
              } catch (error) {
                console.error('❌ Test notification failed:', error);
                alert('❌ Test notification failed. Check console for details.');
              }
            }}
            style={{
              background: '#17a2b8',
              color: 'white',
              border: 'none',
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '12px',
              cursor: 'pointer',
              marginLeft: '8px'
            }}
          >
            🔔 TEST NOTIFICATION
          </button>
          <button 
            onClick={async () => {
              try {
                console.log('💾 Force saving to localStorage...');
                const dataToSave = {
                  sheets,
                  activeSheetIndex,
                  columnWidths,
                  rowHeights,
                  lastSaved: new Date().toISOString()
                };
                localStorage.setItem(`excel_workbook_${folderId}`, JSON.stringify(dataToSave));
                console.log('✅ Data saved to localStorage');
                
                // Also save to server
                await handleAutoSave();
                alert('✅ Data saved to both localStorage and server!');
              } catch (error) {
                console.error('❌ Force save failed:', error);
                alert('❌ Force save failed. Check console for details.');
              }
            }}
            style={{
              background: '#6f42c1',
              color: 'white',
              border: 'none',
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '12px',
              cursor: 'pointer',
              marginLeft: '8px'
            }}
          >
            💾 FORCE SAVE
          </button>
        </div>
      </div>

      {/* Add Sheet Modal */}
      {showAddSheetModal && (
        <div className="modal-overlay" onClick={() => setShowAddSheetModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Add New Sheet</h3>
            <input
              type="text"
              value={newSheetName}
              onChange={(e) => setNewSheetName(e.target.value)}
              placeholder="Enter sheet name"
              className="modal-input"
              autoFocus
            />
            <div className="modal-actions">
              <button onClick={handleAddSheet} className="btn-primary">Add Sheet</button>
              <button onClick={() => setShowAddSheetModal(false)} className="btn-secondary">Cancel</button>
            </div>
          </div>
        </div>
      )}


      {/* Upload File Modal */}
      {showUploadModal && (
        <div className="modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Upload File</h3>
            <p>Upload CSV or Excel files to create new sheets</p>
            <input
              type="file"
              multiple
              accept=".csv,.xlsx,.xls"
              onChange={(e) => e.target.files && handleFileUpload(e.target.files)}
              className="file-input"
            />
            <div className="modal-actions">
              <button onClick={() => setShowUploadModal(false)} className="btn-secondary">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Context Menu */}
      {showContextMenu && (
        <div
          className="context-menu"
          style={{
            position: 'fixed',
            left: showContextMenu.x,
            top: showContextMenu.y,
            zIndex: 1000
          }}
          onClick={() => setShowContextMenu(null)}
        >
          <div className="context-menu-item" onClick={handleCopy}>Copy</div>
          <div 
            className={`context-menu-item ${!clipboard ? 'disabled' : ''}`} 
            onClick={clipboard ? handlePaste : undefined}
          >
            Paste
          </div>
          <div className="context-menu-divider"></div>
          <div className="context-menu-item" onClick={handleAddRow}>Insert Row</div>
          <div className="context-menu-item" onClick={handleAddColumn}>Insert Column</div>
          <div className="context-menu-item" onClick={handleDeleteRow}>Delete Row</div>
          <div className="context-menu-item" onClick={handleDeleteColumn}>Delete Column</div>
        </div>
      )}

      {/* Header Import Modal */}
      <HeaderImportModal
        isOpen={showHeaderImportModal}
        onClose={() => setShowHeaderImportModal(false)}
        onImportHeaders={handleImportHeaders}
        currentHeaders={sheets[activeSheetIndex]?.data[0]?.map(cell => String(cell?.value || '')) || []}
      />
    </div>
  );
};

export default FullExcelEditor;